import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, Download, Eraser, FilePlus2, IndianRupee, Lock, LogOut, Mail, Plus, Printer, Receipt, RefreshCw, RotateCcw, Search, Send, Settings, StickyNote, Trash2, Unlock, X } from "lucide-react";
import { toast } from "sonner";
import MacJitLogo from "../components/MacJitLogo";
import { useAuth } from "../context/AuthContext";
import api, { backendUrl } from "../lib/api";

const periods = {
  today: "Today",
  week: "7 days",
  month: "Month",
  custom: "Custom",
};

const emptyItem = () => ({ id: `${Date.now()}-${Math.random()}`, name: "", qty: "1", price: "" });
const emptyManualLine = () => ({ id: `${Date.now()}-${Math.random()}`, label: "", amount: "" });
const emptyCreditItem = () => ({ id: `${Date.now()}-${Math.random()}`, name: "", qty: "1", price: "" });
const randomStickyColor = () => {
  const colors = ["amber", "rose", "sky", "mint", "violet"];
  return colors[Math.floor(Math.random() * colors.length)];
};
const emptyStickyPage = () => ({ id: `${Date.now()}-${Math.random()}`, title: "", text: "", color: randomStickyColor(), locked: false, createdAt: "", updatedAt: "" });
const stickyColors = {
  amber: "from-amber-100 to-yellow-200 text-zinc-950 border-amber-300",
  rose: "from-rose-100 to-pink-200 text-zinc-950 border-rose-300",
  sky: "from-sky-100 to-cyan-200 text-zinc-950 border-sky-300",
  mint: "from-emerald-100 to-lime-200 text-zinc-950 border-emerald-300",
  violet: "from-violet-100 to-fuchsia-200 text-zinc-950 border-violet-300",
};
const normalizeStickyPage = (page = {}) => ({
  id: page.id || page.client_id || `${Date.now()}-${Math.random()}`,
  client_id: page.client_id || page.id || `${Date.now()}-${Math.random()}`,
  title: page.title || "",
  text: page.text || "",
  color: page.color || randomStickyColor(),
  locked: Boolean(page.locked),
  has_pin: Boolean(page.has_pin),
  createdAt: page.createdAt || "",
  updatedAt: page.updatedAt || "",
});
const defaultTaxRules = [
  { key: "cgst", label: "CGST", type: "percentage", rate: 9, scope: "shop", active: true },
  { key: "sgst", label: "SGST", type: "percentage", rate: 9, scope: "shop", active: true },
];
const gatewayDocs = {
  razorpay: [
    "1. Login to the merchant's Razorpay dashboard.",
    "2. Open Settings > API Keys and generate test or live key id and secret.",
    "3. Open Webhooks and add your Terminal URL callback when required. If any website URL is requested, use your Terminal URL.",
    "4. Paste key id, key secret, and webhook secret here, then activate Razorpay.",
    "5. Test with a small bill before switching to live mode.",
  ],
};
const providerFieldConfig = {
  razorpay: {
    keyLabel: "Razorpay Key ID",
    secretLabel: "Razorpay Key Secret",
    webhookLabel: "Webhook secret",
    keyRequiredMessage: "Razorpay Key ID is required",
    secretRequiredMessage: "Razorpay Key Secret is required",
    extra: [],
  },
};

function providerLabel(provider) {
  return "Razorpay";
}

export default function LedgerPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("bill");
  const [busy, setBusy] = useState(false);
  const [stickyOpen, setStickyOpen] = useState(false);
  const [stickyIndex, setStickyIndex] = useState(0);
  const [customerLedger, setCustomerLedger] = useState({ open: false, loading: false, data: null, seed: null });
  const [receiptRow, setReceiptRow] = useState(null);
  const [editBill, setEditBill] = useState(null);
  const [products, setProducts] = useState([]);
  const [stickyPages, setStickyPages] = useState([emptyStickyPage()]);
  const [stickyLoaded, setStickyLoaded] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [credits, setCredits] = useState([]);
  const [paymentIssues, setPaymentIssues] = useState([]);
  const [ledgerRefunds, setLedgerRefunds] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [storageUsage, setStorageUsage] = useState(null);
  const [storageArchives, setStorageArchives] = useState([]);
  const [archiveViewer, setArchiveViewer] = useState(null);
  const [storageDraft, setStorageDraft] = useState({ auto_archive_enabled: false, archive_after_days: 365, purge_after_days: 30 });
  const [subscription, setSubscription] = useState(null);
  const [selectedBills, setSelectedBills] = useState([]);
  const [routeMerchants, setRouteMerchants] = useState([]);
  const [savedGateways, setSavedGateways] = useState([]);
  const [summary, setSummary] = useState({});
  const [settings, setSettings] = useState({ rules: defaultTaxRules });
  const [routeDraft, setRouteDraft] = useState({
    provider: "razorpay",
    mode: "test",
    key_id: "",
    key_secret: "",
    webhook_secret: "",
  });
  const [filters, setFilters] = useState({ q: "", period: "today", date_from: "", date_to: "" });
  const [draft, setDraft] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    payment_mode: "cash",
    items: [emptyItem()],
    manual_lines: [],
    tax_rules: [],
  });
  const [creditDraft, setCreditDraft] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    paid_amount: "",
    note: "",
    items: [emptyCreditItem()],
  });
  const [creditPayments, setCreditPayments] = useState({});

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/transactions", {
        params: {
          type: "all",
          q: filters.q,
          period: filters.period,
          date_from: filters.date_from,
          date_to: filters.date_to,
          limit: 200,
        },
      });
      setTransactions(res.data?.transactions || []);
      setSummary(res.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load ledger history");
    } finally {
      setBusy(false);
    }
  }, [filters.date_from, filters.date_to, filters.period, filters.q]);

  useEffect(() => { load(); }, [load]);
  const loadCredits = useCallback(async () => {
    try {
      const res = await api.get("/ledger/credits");
      setCredits(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load credit notes");
    }
  }, []);
  useEffect(() => { loadCredits(); }, [loadCredits]);
  const loadPaymentOps = useCallback(async () => {
    try {
      const [issuesRes, refundsRes, settlementsRes] = await Promise.all([
        api.get("/ledger/payment-issues"),
        api.get("/ledger/refunds"),
        api.get("/ledger/settlements"),
      ]);
      setPaymentIssues(Array.isArray(issuesRes.data) ? issuesRes.data : []);
      setLedgerRefunds(Array.isArray(refundsRes.data) ? refundsRes.data : []);
      setSettlements(Array.isArray(settlementsRes.data) ? settlementsRes.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load payment operations");
    }
  }, []);
  useEffect(() => { loadPaymentOps(); }, [loadPaymentOps]);
  const loadStorageUsage = useCallback(async () => {
    try {
      const res = await api.get("/ledger/storage");
      setStorageUsage(res.data || null);
      setStorageDraft({
        auto_archive_enabled: Boolean(res.data?.settings?.auto_archive_enabled),
        archive_after_days: Number(res.data?.settings?.archive_after_days || 365),
        purge_after_days: Number(res.data?.settings?.purge_after_days || 30),
      });
    } catch (err) {
      setStorageUsage(null);
    }
  }, []);
  useEffect(() => { loadStorageUsage(); }, [loadStorageUsage]);
  const loadStorageArchives = useCallback(async () => {
    try {
      const res = await api.get("/ledger/storage/archives");
      setStorageArchives(Array.isArray(res.data) ? res.data : []);
    } catch {
      setStorageArchives([]);
    }
  }, []);
  useEffect(() => { loadStorageArchives(); }, [loadStorageArchives]);
  const loadSubscription = useCallback(async () => {
    try {
      const res = await api.get("/admin/subscription/me");
      setSubscription(res.data || null);
    } catch {
      setSubscription(null);
    }
  }, []);
  useEffect(() => { loadSubscription(); }, [loadSubscription]);
  const loadRouteMerchants = useCallback(async () => {
    try {
      const res = await api.get("/payments/gateway-settings");
      setRouteMerchants(res.data?.active ? [res.data] : []);
      setSavedGateways(Array.isArray(res.data?.saved_gateways) ? res.data.saved_gateways : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load payment setup");
    }
  }, []);
  useEffect(() => { loadRouteMerchants(); }, [loadRouteMerchants]);
  const loadStickyPages = useCallback(async () => {
    try {
      const res = await api.get("/ledger/sticky-pages");
      const rows = Array.isArray(res.data) && res.data.length ? res.data : [emptyStickyPage()];
      setStickyPages(rows.map(normalizeStickyPage));
      setStickyLoaded(true);
    } catch {
      setStickyPages([emptyStickyPage()]);
      setStickyLoaded(true);
    }
  }, []);
  useEffect(() => { loadStickyPages(); }, [loadStickyPages]);
  useEffect(() => {
    if (!stickyLoaded) return;
    const timer = window.setTimeout(() => {
      api.put("/ledger/sticky-pages", { pages: stickyPages }).catch(() => {});
    }, 500);
    return () => window.clearTimeout(timer);
  }, [stickyPages, stickyLoaded]);
  useEffect(() => {
    api.get("/admin/billing-settings")
      .then((res) => setSettings(normalizeSettings(res.data || {})))
      .catch(() => setSettings({ rules: defaultTaxRules }));
    api.get("/ledger/products")
      .then((res) => setProducts(Array.isArray(res.data) ? res.data : []))
      .catch(() => setProducts([]));
  }, []);

  const miniTransactions = useMemo(() => transactions.filter((row) => row.kind === "ledger"), [transactions]);
  const refunds = useMemo(() => transactions.filter((row) => row.kind === "refund" && (row.extra?.ledger_bill_id || row.extra?.sale_kind === "ledger")), [transactions]);
  const paidRows = miniTransactions.filter((row) => row.paid || String(row.status || "").toUpperCase() === "PAID");
  const totalPaid = paidRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const totalRefund = refunds.reduce((sum, row) => sum + Math.abs(Number(row.amount || 0)), 0);
  const cashCount = paidRows.filter((row) => String(row.method || "").toLowerCase() === "cash").length;
  const onlineCount = paidRows.filter((row) => ["online", "razorpay", "payment link", "upi"].some((mode) => String(row.method || "").toLowerCase().includes(mode))).length;
  const otherPaidCount = Math.max(0, paidRows.length - cashCount - onlineCount);
  const creditIssued = credits.reduce((sum, row) => sum + Number(row.credit_amount || 0), 0);
  const creditPending = credits.reduce((sum, row) => sum + Number(row.balance_amount || 0), 0);
  const creditSettled = credits.reduce((sum, row) => sum + Number(row.paid_amount || 0), 0);
  const creditOpenCount = credits.filter((row) => row.status !== "PAID").length;
  const activeGateway = routeMerchants[0] || {};
  const routeMerchantActive = Boolean(activeGateway.active);
  const gatewayLabel = activeGateway.provider_label || providerLabel(routeDraft.provider);
  const trialLocked = subscription?.status === "trial_exhausted";
  const storageLocked = Boolean(storageUsage?.write_locked) || trialLocked;
  const historyRows = useMemo(() => {
    const openCreditRows = credits
      .filter((credit) => credit.status !== "PAID")
      .map((credit) => ({
        kind: "credit",
        id: credit.id,
        ref: credit.transaction_ref || credit.id,
        customer_name: credit.customer_name,
        customer_phone: credit.customer_phone,
        customer_email: credit.customer_email,
        title: (credit.items || []).length === 1 ? credit.items[0].name : `${(credit.items || []).length || 1} item credit sale`,
        amount: Number(credit.balance_amount || credit.credit_amount || 0),
        ts: credit.credit_started_at || credit.created_at,
        method: "Credit pending",
        items: credit.items || [],
        extra: { status: credit.status, balance_amount: credit.balance_amount },
      }));
    return [...openCreditRows, ...miniTransactions, ...refunds]
      .sort((a, b) => String(b.ts || "").localeCompare(String(a.ts || "")));
  }, [credits, miniTransactions, refunds]);
  const visibleBillIds = useMemo(() => historyRows.filter((row) => row.kind === "ledger").map((row) => row.id), [historyRows]);
  const billItems = draft.items || [];
  const creditItems = creditDraft.items || [];
  const billTotal = billItems.reduce((sum, item) => sum + Number(item.qty || 0) * Number(item.price || 0), 0);
  const manualLines = draft.manual_lines || [];
  const hasGstin = Boolean(String(settings.gstin || "").trim());
  const settingsTaxRules = (settings.rules || []).filter((rule) => rule.active !== false && ["shop", "both"].includes(String(rule.scope || "both").toLowerCase()));
  const billTaxRules = hasGstin ? settingsTaxRules : [];
  const activeTaxRules = hasGstin ? billTaxRules.filter((rule) => rule.active !== false) : [];
  const manualTotal = manualLines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
  const taxPreview = activeTaxRules.map((rule) => {
    const rate = Number(rule.rate || 0);
    const amount = String(rule.type || "percentage") === "fixed" ? rate : Math.max(0, billTotal + manualTotal) * rate / 100;
    return { ...rule, amount };
  }).filter((rule) => Number(rule.amount || 0) !== 0);
  const taxTotal = taxPreview.reduce((sum, rule) => sum + Number(rule.amount || 0), 0);
  const grandTotal = Math.max(0, billTotal + manualTotal + taxTotal);
  const validItems = billItems
    .map((item) => ({
      name: String(item.name || "").trim(),
      qty: Number(item.qty || 0),
      price: Number(item.price || 0),
    }))
    .filter((item) => item.name && item.qty > 0 && item.price > 0);
  const validCreditItems = creditItems
    .map((item) => ({ name: String(item.name || "").trim(), qty: Number(item.qty || 0), price: Number(item.price || 0) }))
    .filter((item) => item.name && item.qty > 0 && item.price > 0);
  const creditTotal = validCreditItems.reduce((sum, item) => sum + item.qty * item.price, 0);
  const updDraft = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const updFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));
  const updateItem = (id, key, value) => setDraft((current) => ({
    ...current,
    items: (current.items || []).map((item) => {
      if (item.id !== id) return item;
      if (key === "name") {
        const match = products.find((product) => String(product.name || "").toLowerCase() === String(value || "").toLowerCase());
        return match ? { ...item, name: value, price: String(Math.round(Number(match.price || 0))) } : { ...item, name: value };
      }
      return { ...item, [key]: value };
    }),
  }));
  const addItem = () => setDraft((current) => ({ ...current, items: [...(current.items || []), emptyItem()] }));
  const removeItem = (id) => setDraft((current) => {
    const next = (current.items || []).filter((item) => item.id !== id);
    return { ...current, items: next.length ? next : [emptyItem()] };
  });
  const updateManualLine = (id, key, value) => setDraft((current) => ({
    ...current,
    manual_lines: (current.manual_lines || []).map((line) => line.id === id ? { ...line, [key]: value } : line),
  }));
  const addManualLine = () => setDraft((current) => ({ ...current, manual_lines: [...(current.manual_lines || []), emptyManualLine()] }));
  const removeManualLine = (id) => setDraft((current) => ({ ...current, manual_lines: (current.manual_lines || []).filter((line) => line.id !== id) }));
  const updateCreditDraft = (key, value) => setCreditDraft((current) => ({ ...current, [key]: value }));
  const updateCreditItem = (id, key, value) => setCreditDraft((current) => ({
    ...current,
    items: (current.items || []).map((item) => item.id === id ? { ...item, [key]: value } : item),
  }));
  const addCreditItem = () => setCreditDraft((current) => ({ ...current, items: [...(current.items || []), emptyCreditItem()] }));
  const removeCreditItem = (id) => setCreditDraft((current) => {
    const next = (current.items || []).filter((item) => item.id !== id);
    return { ...current, items: next.length ? next : [emptyCreditItem()] };
  });

  const resetDraft = () => setDraft({ customer_name: "", customer_email: "", customer_phone: "", payment_mode: "cash", items: [emptyItem()], manual_lines: [], tax_rules: [] });
  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });
  const openMiniRouteCheckout = async (bill) => {
    const checkout = bill.route_checkout || {};
    if (bill.payment_link && (!checkout.order_id || !checkout.key_id)) {
      window.open(bill.payment_link, "_blank", "noopener,noreferrer");
      return true;
    }
    if (!checkout.order_id || !checkout.key_id) return false;
    const ok = await loadRazorpay();
    if (!ok) throw new Error("Could not load Razorpay. Check your internet connection.");
    await new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: checkout.key_id,
        amount: checkout.amount,
        currency: checkout.currency || "INR",
        name: checkout.name || "Terminal Mini",
        description: checkout.description || "Mini counter bill",
        order_id: checkout.order_id,
        prefill: checkout.prefill || {},
        theme: { color: "#f97316" },
        modal: {
          ondismiss: () => {
            api.post(`/ledger/bills/${bill.id}/payment-failed`, { reason: "Payment cancelled" }).then(() => load()).catch(() => {});
            reject(new Error("Payment cancelled"));
          },
        },
        handler: async (resp) => {
          try {
            await api.post(`/ledger/bills/${bill.id}/route/verify`, {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful. Invoice generated.");
            await load();
            await loadPaymentOps();
            resolve();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
            reject(err);
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp.error?.description || "Payment failed");
        api.post(`/ledger/bills/${bill.id}/payment-failed`, { reason: resp.error?.description || "Payment failed" }).catch(() => {});
        load();
        reject(new Error("Payment failed"));
      });
      rzp.open();
    });
    return true;
  };
  const submitDraft = async (mode) => {
    if (storageLocked) {
      setTab("history");
      if (trialLocked && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("macjit:subscribe-required", {
          detail: { message: "Your 99 free Mini transactions are complete. Subscribe now to continue creating bills." },
        }));
      }
      return toast.error(trialLocked ? "Your 99 free Mini transactions are complete. Subscribe to continue billing." : "Mini storage is full. Archive or delete old records before creating a new bill.");
    }
    if (!validItems.length) return toast.error("Add at least one product with qty and price");
    if ((mode === "razorpay" || mode === "link") && !routeMerchantActive) {
      setTab("profile");
      return toast.error("Complete Razorpay Route verification before collecting online payments");
    }
    setBusy(true);
    try {
      if (mode === "counter" && draft.payment_mode === "credit") {
        await api.post("/ledger/credits", {
          customer_name: draft.customer_name || "Credit Customer",
          customer_email: draft.customer_email,
          customer_phone: draft.customer_phone,
          customer_address: "",
          paid_amount: 0,
          credit_amount: grandTotal,
          note: "Created from Mini create bill",
          items: validItems,
        });
        toast.success("Credit sale saved and synced with Credit sales");
        resetDraft();
        setTab("credits");
        loadCredits();
        load();
        return;
      }
      const res = await api.post("/ledger/bills", {
        title: validItems.length === 1 ? validItems[0].name : `${validItems.length} item counter bill`,
        total: billTotal,
        items: validItems,
        manual_lines: manualLines
          .map((line) => ({ label: String(line.label || "").trim(), amount: Number(line.amount || 0) }))
          .filter((line) => line.label && line.amount !== 0),
        billing_rules: activeTaxRules
          .map((rule) => ({
            key: String(rule.key || rule.label || "").trim().toLowerCase().replace(/\s+/g, "-"),
            label: String(rule.label || "").trim(),
            type: rule.type === "fixed" ? "fixed" : "percentage",
            rate: Number(rule.rate || 0),
            active: rule.active !== false,
          }))
          .filter((rule) => rule.label && rule.rate !== 0 && rule.active),
        customer_name: draft.customer_name,
        customer_phone: draft.customer_phone,
        customer_email: draft.customer_email,
        payment_method: mode === "link" || mode === "razorpay" ? "online" : draft.payment_mode,
        payment_channel: mode,
      });
      const bill = res.data || {};
      if (mode === "razorpay" || mode === "link") {
        setTab("history");
        load();
        loadPaymentOps();
      }
      if (mode === "razorpay") {
        if (await openMiniRouteCheckout(bill)) {
          toast.success("Payment successful. Transaction history updated.");
        } else if (bill.payment_link) {
          toast.success("Razorpay counter collection opened");
          window.open(bill.payment_link, "_blank", "noopener,noreferrer");
        } else {
          toast.error(bill.route_order_error || "Razorpay counter collection is not ready");
        }
      } else if (mode === "link") {
        toast.success("Payment link created and emailed if customer email was provided");
        if (bill.payment_link) window.open(bill.payment_link, "_blank", "noopener,noreferrer");
      } else {
        toast.success("Counter bill marked paid and invoice email sent if email was provided");
      }
      resetDraft();
      setTab("history");
      load();
      loadPaymentOps();
      loadStorageUsage();
      loadSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create Mini bill");
    } finally {
      setBusy(false);
    }
  };

  const refundRow = async (row) => {
    const amountText = window.prompt("Refund amount", formatMoney(row.amount));
    if (amountText === null) return;
    const amount = Number(amountText || 0);
    if (amount <= 0) return toast.error("Enter refund amount");
    const reason = window.prompt("Refund reason", "Counter refund") || "Counter refund";
    setBusy(true);
    try {
      await api.post(`/ledger/bills/${row.id}/refund`, { amount: Math.abs(amount), reason });
      toast.success("Refund recorded");
      setTab("refunds");
      load();
      loadStorageUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record refund");
    } finally {
      setBusy(false);
    }
  };

  const resendInvoice = async (row) => {
    setBusy(true);
    try {
      await api.post(`/ledger/bills/${row.id}/resend-invoice`);
      toast.success("Invoice email resent");
      load();
      loadStorageUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend invoice");
    } finally {
      setBusy(false);
    }
  };
  const retryPaymentLink = async (bill) => {
    setBusy(true);
    try {
      const res = await api.post(`/ledger/bills/${bill.id}/retry-payment-link`);
      toast.success("Fresh payment link created");
      const link = res.data?.payment_link;
      if (link) window.open(link, "_blank", "noopener,noreferrer");
      loadPaymentOps();
      load();
      loadStorageUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not retry payment link");
    } finally {
      setBusy(false);
    }
  };
  const collectOfflineBill = async (bill) => {
    const method = window.prompt("Collect by which method? Example: Cash, UPI, Card", "Cash");
    if (!method) return;
    const note = window.prompt("Collection note / reference", "") || "";
    setBusy(true);
    try {
      await api.post(`/ledger/bills/${bill.id}/collect-offline`, { payment_method: method, note });
      toast.success("Bill marked paid and invoice generated");
      loadPaymentOps();
      load();
      loadStorageUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark bill paid");
    } finally {
      setBusy(false);
    }
  };
  const syncPaymentStatus = async (bill) => {
    setBusy(true);
    try {
      const res = await api.post(`/ledger/bills/${bill.id}/sync-payment-status`);
      const paid = Boolean(res.data?.paid) || String(res.data?.status || "").toUpperCase() === "PAID";
      toast.success(paid ? "Payment status synced: paid" : `Payment status synced: ${res.data?.status || "pending"}`);
      loadPaymentOps();
      load();
      loadStorageUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sync payment status");
    } finally {
      setBusy(false);
    }
  };
  const openEditBill = (row) => {
    setEditBill({
      id: row.id,
      title: row.title || "",
      customer_name: row.customer_name || "",
      customer_phone: row.customer_phone || "",
      customer_email: row.customer_email || "",
      reason: "",
      items: (row.items?.length ? row.items : [{ name: row.title, qty: 1, price: row.amount, subtotal: row.amount }])
        .map((item) => ({ id: `${Date.now()}-${Math.random()}`, name: item.name || "", qty: String(item.qty || 1), price: String(item.price || item.subtotal || "") })),
      manual_lines: (row.extra?.manual_lines || []).map((line) => ({ id: `${Date.now()}-${Math.random()}`, label: line.label || line.name || "", amount: String(line.amount || "") })),
    });
  };
  const saveEditedBill = async () => {
    if (!editBill?.reason?.trim()) return toast.error("Edit reason is required");
    const items = (editBill.items || [])
      .map((item) => ({ name: String(item.name || "").trim(), qty: Number(item.qty || 0), price: Number(item.price || 0) }))
      .filter((item) => item.name && item.qty > 0 && item.price > 0);
    if (!items.length) return toast.error("Add at least one product with qty and price");
    setBusy(true);
    try {
      await api.patch(`/ledger/bills/${editBill.id}`, {
        title: editBill.title,
        customer_name: editBill.customer_name,
        customer_phone: editBill.customer_phone,
        customer_email: editBill.customer_email,
        reason: editBill.reason,
        items,
        manual_lines: (editBill.manual_lines || [])
          .map((line) => ({ label: String(line.label || "").trim(), amount: Number(line.amount || 0) }))
          .filter((line) => line.label && line.amount !== 0),
      });
      toast.success("Bill updated with audit reason");
      setEditBill(null);
      load();
      loadStorageUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update bill");
    } finally {
      setBusy(false);
    }
  };
  const cancelBill = async (row) => {
    const reason = window.prompt("Cancel reason is required", "Wrong bill created");
    if (!reason?.trim()) return toast.error("Cancel reason is required");
    if (!window.confirm("Cancel this bill? It will be hidden from Mini sales, but kept in audit history.")) return;
    setBusy(true);
    try {
      await api.patch(`/ledger/bills/${row.id}/cancel`, { reason });
      toast.success("Bill cancelled and kept as soft-deleted audit record");
      load();
      loadStorageUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel bill");
    } finally {
      setBusy(false);
    }
  };

  const toggleSelectedBill = (id) => {
    setSelectedBills((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };
  const toggleSelectVisibleBills = () => {
    setSelectedBills((current) => {
      const visible = visibleBillIds.filter(Boolean);
      const selectedVisible = visible.filter((id) => current.includes(id));
      if (visible.length && selectedVisible.length === visible.length) {
        return current.filter((id) => !visible.includes(id));
      }
      return Array.from(new Set([...current, ...visible]));
    });
  };
  const archiveSelectedBills = async () => {
    const ids = selectedBills.filter((id) => visibleBillIds.includes(id));
    if (!ids.length) return toast.error("Select at least one Mini transaction");
    const reason = window.prompt("Archive reason", "Backup and clear old Mini records");
    if (!reason?.trim()) return toast.error("Archive reason is required");
    setBusy(true);
    try {
      const res = await api.post("/ledger/storage/archive", { ids, reason });
      toast.success(`${res.data?.record_count || ids.length} transaction(s) archived`);
      setSelectedBills([]);
      load();
      loadPaymentOps();
      loadStorageUsage();
      loadStorageArchives();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not archive selected transactions");
    } finally {
      setBusy(false);
    }
  };
  const saveStorageSettings = async () => {
    setBusy(true);
    try {
      await api.put("/ledger/storage/settings", storageDraft);
      toast.success("Storage cleanup policy saved");
      loadStorageUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save storage policy");
    } finally {
      setBusy(false);
    }
  };
  const runAutoCleanup = async () => {
    if (!window.confirm("Archive records older than your cleanup policy now?")) return;
    setBusy(true);
    try {
      const res = await api.post("/ledger/storage/auto-cleanup");
      toast.success(`${res.data?.record_count || res.data?.archived || 0} old transaction(s) archived`);
      load();
      loadStorageUsage();
      loadStorageArchives();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not run auto cleanup");
    } finally {
      setBusy(false);
    }
  };
  const downloadArchive = async (archive) => {
    if (!archive?.id) return toast.error("Archive id is missing");
    setBusy(true);
    try {
      const res = await api.get(`/ledger/storage/archives/${archive.id}.csv`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `mini-archive-${archive.id}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Archive CSV downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not download archive");
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = (type) => window.open(backendUrl(`/api/ledger/export.csv?type=${type}`), "_blank", "noopener,noreferrer");

  const createCredit = async () => {
    if (!creditDraft.customer_name.trim()) return toast.error("Customer name is required");
    if (!validCreditItems.length) return toast.error("Add credit product list");
    setBusy(true);
    try {
      await api.post("/ledger/credits", {
        customer_name: creditDraft.customer_name,
        customer_email: creditDraft.customer_email,
        customer_phone: creditDraft.customer_phone,
        customer_address: creditDraft.customer_address,
        paid_amount: Number(creditDraft.paid_amount || 0),
        credit_amount: creditTotal,
        note: creditDraft.note,
        items: validCreditItems,
      });
      toast.success("Credit sale note saved");
      setCreditDraft({ customer_name: "", customer_email: "", customer_phone: "", customer_address: "", paid_amount: "", note: "", items: [emptyCreditItem()] });
      loadCredits();
      load();
      loadStorageUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save credit sale");
    } finally {
      setBusy(false);
    }
  };

  const updateCreditPayment = (id, key, value) => setCreditPayments((current) => ({ ...current, [id]: { ...(current[id] || {}), [key]: value } }));

  const payCredit = async (credit, full = false) => {
    const draftPayment = creditPayments[credit.id] || {};
    const balance = Number(credit.balance_amount || 0);
    const amount = full ? balance : Number(draftPayment.amount || 0);
    if (amount <= 0) return toast.error("Enter credit payment amount");
    setBusy(true);
    try {
      await api.patch(`/ledger/credits/${credit.id}`, {
        payment_amount: amount,
        payment_method: draftPayment.method || "cash",
        payment_note: draftPayment.note || "",
      });
      toast.success(full || amount >= balance ? "Credit settled and added to transactions" : "Credit payment recorded");
      setCreditPayments((current) => ({ ...current, [credit.id]: { amount: "", method: draftPayment.method || "cash", note: "" } }));
      loadCredits();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update credit");
    } finally {
      setBusy(false);
    }
  };
  const sendCreditPaymentLink = async (credit, full = false) => {
    if (!routeMerchantActive) {
      setTab("profile");
      return toast.error("Complete payment gateway activation in Merchant profile before sending credit payment links");
    }
    const draftPayment = creditPayments[credit.id] || {};
    const balance = Number(credit.balance_amount || 0);
    const amount = full ? balance : Number(draftPayment.amount || balance);
    if (amount <= 0) return toast.error("Enter payment link amount");
    const email = String(draftPayment.email || credit.customer_email || "").trim().toLowerCase();
    if (!email) return toast.error("Add customer email before sending payment link");
    setBusy(true);
    try {
      const res = await api.post(`/ledger/credits/${credit.id}/payment-link`, { amount, customer_email: email });
      const link = res.data?.payment_link;
      toast.success("Credit payment link created and emailed");
      if (link) window.open(link, "_blank", "noopener,noreferrer");
      setCreditPayments((current) => ({ ...current, [credit.id]: { ...(current[credit.id] || {}), amount: "", email } }));
      loadCredits();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create credit payment link");
    } finally {
      setBusy(false);
    }
  };

  const openCustomerLedger = async (customer) => {
    const seed = {
      name: customer?.customer_name || customer?.name || "",
      phone: customer?.customer_phone || customer?.phone || "",
      email: customer?.customer_email || customer?.email || "",
    };
    setCustomerLedger({ open: true, loading: true, data: null, seed });
    try {
      const res = await api.get("/ledger/customer-ledger", { params: seed });
      setCustomerLedger({ open: true, loading: false, data: res.data || {}, seed });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load customer ledger");
      setCustomerLedger({ open: true, loading: false, data: null, seed });
    }
  };
  const updateStickyPage = (patch) => setStickyPages((current) => current.map((page, index) => {
    if (index !== stickyIndex || page.locked) return page;
    const next = { ...page, ...patch };
    const hasContent = String(next.title || "").trim() || String(next.text || "").trim();
    const now = new Date().toISOString();
    return { ...next, createdAt: next.createdAt || (hasContent ? now : ""), updatedAt: hasContent ? now : next.updatedAt };
  }));
  const addStickyPage = () => {
    setStickyPages((current) => [...current, emptyStickyPage()]);
    setStickyIndex(stickyPages.length);
    setStickyOpen(true);
  };
  const removeStickyPage = () => {
    setStickyPages((current) => {
      const next = current.filter((_, index) => index !== stickyIndex);
      setStickyIndex(Math.max(0, Math.min(stickyIndex, next.length - 1)));
      return next.length ? next : [emptyStickyPage()];
    });
  };
  const eraseStickyPage = () => updateStickyPage({ title: "", text: "", createdAt: "", updatedAt: "" });
  const deleteAllStickyPages = () => {
    if (!window.confirm("Delete all sticky note pages?")) return;
    setStickyPages([emptyStickyPage()]);
    setStickyIndex(0);
  };
  const toggleStickyLock = async ({ pin = "", pinLock = false } = {}) => {
    const page = stickyPages[stickyIndex];
    if (!page) return null;
    if ((pinLock || page.has_pin) && !/^\d{4}$/.test(pin)) {
      toast.error("Enter a valid 4 digit PIN");
      return null;
    }
    try {
      const action = page.locked ? "unlock" : "lock";
      const res = await api.post(`/ledger/sticky-pages/${page.id || page.client_id}/${action}`, { pin: page.locked || pinLock ? pin : "" });
      const updated = normalizeStickyPage(res.data || {});
      setStickyPages((current) => current.map((item, index) => index === stickyIndex ? { ...item, ...updated } : item));
      toast.success(page.locked ? "Sticky page unlocked" : "Sticky page locked");
      return updated;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update sticky page lock");
      return null;
    }
  };
  const saveRouteMerchant = async () => {
    const fieldConfig = providerFieldConfig[routeDraft.provider] || providerFieldConfig.razorpay;
    if (!routeDraft.key_id.trim()) return toast.error(fieldConfig.keyRequiredMessage);
    if (!routeDraft.key_secret.trim()) return toast.error(fieldConfig.secretRequiredMessage);
    setBusy(true);
    try {
      await api.put("/payments/gateway-settings", routeDraft);
      toast.success(`${providerLabel(routeDraft.provider)} activated for Mini online billing`);
      setRouteDraft((current) => ({
        ...current,
        key_secret: "",
        webhook_secret: "",
      }));
      loadRouteMerchants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit payment setup");
    } finally {
      setBusy(false);
    }
  };
  const retryRouteMerchant = async (merchant) => {
    setBusy(true);
    try {
      toast.message("Use the provider documentation steps, then save the latest key and secret again.");
      loadRouteMerchants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not retry Route setup");
    } finally {
      setBusy(false);
    }
  };
  const activateSavedGateway = async (provider) => {
    setBusy(true);
    try {
      await api.post(`/payments/gateway-settings/${provider}/activate`);
      toast.success(`${providerLabel(provider)} activated for Mini online billing`);
      loadRouteMerchants();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not activate ${providerLabel(provider)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="sticky top-0 z-20 border-b border-zinc-800 bg-zinc-950/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-3">
            <MacJitLogo size={34} />
            <div>
              <p className="font-display text-lg font-black tracking-tighter">TERMINAL <span className="text-orange-500">/ LEDGER</span></p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{user?.name || "Billing desk"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={busy} className="border border-zinc-800 p-2 text-zinc-300 hover:border-orange-500 hover:text-orange-500 disabled:opacity-50" aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={logout} className="border border-zinc-800 p-2 text-zinc-300 hover:border-orange-500 hover:text-orange-500" aria-label="Logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-5 sm:px-6 sm:py-8">
        <section className="order-2 hidden gap-px bg-zinc-800 md:order-1 md:grid md:grid-cols-3 xl:grid-cols-6">
          <LedgerMetric icon={IndianRupee} label="Collected" value={`₹${Math.round(totalPaid)}`} sub={`${paidRows.length} paid records`} />
          <LedgerMetric icon={RotateCcw} label="Refunds" value={`₹${Math.round(totalRefund)}`} sub={`${refunds.length} refund records`} />
          <LedgerMetric icon={IndianRupee} label="Paid bills" value={paidRows.length} sub={`${cashCount} cash · ${onlineCount} online${otherPaidCount ? ` · ${otherPaidCount} other` : ""}`} />
          <LedgerMetric icon={IndianRupee} label="Final sale" value={`₹${Math.round(totalPaid - totalRefund)}`} sub="Mini paid bills - Mini refunds" />
          <LedgerMetric icon={IndianRupee} label="Credit issued" value={`₹${Math.round(creditIssued)}`} sub={`${credits.length} credit records`} />
          <LedgerMetric icon={IndianRupee} label="Credit pending" value={`₹${Math.round(creditPending)}`} sub={`${creditOpenCount} unpaid credits`} />
        </section>
        <div className="order-3 hidden md:order-2 md:block">
          <MiniBars transactions={[...miniTransactions, ...refunds]} credits={credits} />
        </div>

        <section className="order-1 border border-zinc-800 bg-zinc-900/40 md:order-3">
          <div className="flex overflow-x-auto border-b border-zinc-800">
            <LedgerTab className="md:hidden" active={tab === "dashboard"} onClick={() => setTab("dashboard")}>Dashboard</LedgerTab>
            <LedgerTab active={tab === "bill"} onClick={() => setTab("bill")}>Create bill</LedgerTab>
            <LedgerTab className="md:hidden" active={tab === "billing"} onClick={() => setTab("billing")}>Adjustments</LedgerTab>
            <LedgerTab active={tab === "history"} onClick={() => setTab("history")}>Transaction history</LedgerTab>
            <LedgerTab active={tab === "refunds"} onClick={() => setTab("refunds")}>Refunds</LedgerTab>
            <LedgerTab active={tab === "failed"} onClick={() => setTab("failed")}>Failed payments</LedgerTab>
            <LedgerTab active={tab === "settlements"} onClick={() => setTab("settlements")}>Settlements</LedgerTab>
            <LedgerTab active={tab === "credits"} onClick={() => setTab("credits")}>Credit sales</LedgerTab>
            <LedgerTab active={tab === "profile"} onClick={() => setTab("profile")}>Merchant profile</LedgerTab>
          </div>

          {tab === "dashboard" && (
            <div className="space-y-5 p-4 md:hidden">
              <section className="grid gap-px bg-zinc-800">
                <LedgerMetric icon={IndianRupee} label="Collected" value={`₹${Math.round(totalPaid)}`} sub={`${paidRows.length} paid records`} />
                <LedgerMetric icon={RotateCcw} label="Refunds" value={`₹${Math.round(totalRefund)}`} sub={`${refunds.length} refund records`} />
                <LedgerMetric icon={IndianRupee} label="Paid bills" value={paidRows.length} sub={`${cashCount} cash · ${onlineCount} online${otherPaidCount ? ` · ${otherPaidCount} other` : ""}`} />
                <LedgerMetric icon={IndianRupee} label="Final sale" value={`₹${Math.round(totalPaid - totalRefund)}`} sub="Mini paid bills - Mini refunds" />
                <LedgerMetric icon={IndianRupee} label="Credit issued" value={`₹${Math.round(creditIssued)}`} sub={`${credits.length} credit records`} />
                <LedgerMetric icon={IndianRupee} label="Credit pending" value={`₹${Math.round(creditPending)}`} sub={`${creditOpenCount} unpaid credits`} />
              </section>
              <MiniBars transactions={[...miniTransactions, ...refunds]} credits={credits} />
              <MiniTrialMeter subscription={subscription} />
            </div>
          )}

          {tab === "bill" && (
            <div className="space-y-5 p-5">
              <div className="hidden md:block">
                <MiniTrialMeter subscription={subscription} />
              </div>
              {trialLocked && (
                <div className="border border-orange-500/40 bg-orange-500/10 p-4 text-sm text-orange-100 md:hidden">
                  Your 99 free Mini transactions are complete. Payment collection is disabled until you subscribe.
                </div>
              )}
              <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
                <div className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-3">
                  <LedgerField label="Customer name" value={draft.customer_name} onChange={(v) => updDraft("customer_name", v)} />
                  <LedgerField label="Mobile" value={draft.customer_phone} onChange={(v) => updDraft("customer_phone", v)} />
                  <LedgerField label="Email for invoice" type="email" value={draft.customer_email} onChange={(v) => updDraft("customer_email", v.toLowerCase())} />
                </div>
                <div className="border border-zinc-800 bg-zinc-950/70">
                  <div className="flex items-center justify-between gap-3 border-b border-zinc-800 px-4 py-3">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-orange-500">Line items</p>
                      <p className="mt-1 text-xs text-zinc-500">Add every product or service in this counter bill.</p>
                    </div>
                    <button type="button" onClick={addItem} className="inline-flex items-center gap-2 border border-orange-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black">
                      <Plus className="h-3.5 w-3.5" /> Add
                    </button>
                  </div>
                  <div className="divide-y divide-zinc-800">
                    {billItems.map((item, index) => (
                      <div key={item.id} className="grid gap-3 p-4 sm:grid-cols-[1fr_90px_120px_42px] sm:items-end">
                        <LedgerField label={`Item ${index + 1}`} value={item.name} onChange={(v) => updateItem(item.id, "name", v)} list="mini-products" />
                        <LedgerField label="Qty" type="number" value={item.qty} onChange={(v) => updateItem(item.id, "qty", v)} />
                        <LedgerField label="Price" type="number" value={item.price} onChange={(v) => updateItem(item.id, "price", v)} />
                        <button type="button" onClick={() => removeItem(item.id)} className="h-[46px] border border-zinc-800 text-zinc-500 hover:border-red-400 hover:text-red-300" aria-label="Remove item">
                          <Trash2 className="mx-auto h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <datalist id="mini-products">
                  {products.map((product) => <option key={product.name} value={product.name}>{`₹${Math.round(Number(product.price || 0))}`}</option>)}
                </datalist>
                <div className="hidden md:block">
                  <BillAdjustmentConfig
                    manualLines={manualLines}
                    addManualLine={addManualLine}
                    updateManualLine={updateManualLine}
                    removeManualLine={removeManualLine}
                  />
                </div>
                <label className="block max-w-xs">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Counter payment mode</span>
                  <select value={draft.payment_mode} onChange={(e) => updDraft("payment_mode", e.target.value)} className="mt-2 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500">
                    <option value="cash">Cash</option>
                    <option value="upi">UPI collected</option>
                    <option value="card">Card collected</option>
                    <option value="credit">Credit sale</option>
                  </select>
                </label>
                </div>
                <div className="border border-zinc-800 bg-gradient-to-b from-zinc-950 to-black p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Bill preview</p>
                <div className="mt-5 rounded-sm border border-zinc-800 bg-zinc-950/80 p-4">
                  <p className="font-display text-xl font-semibold tracking-tight text-zinc-100">{draft.customer_name || "Walk-in customer"}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">{draft.payment_mode} counter bill · {settings.shop_name || settings.legal_name || "Shop profile"}</p>
                  {(settings.gstin || settings.invoice_prefix) && (
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-600">
                      {settings.gstin ? `GSTIN ${settings.gstin} · ` : ""}{settings.invoice_prefix || "MINI"}
                    </p>
                  )}
                  <div className="mt-5 space-y-3">
                    {validItems.length ? validItems.map((item, index) => (
                      <div key={`${item.name}-${index}`} className="flex items-start justify-between gap-3 border-b border-zinc-900 pb-3 last:border-b-0">
                        <div>
                          <p className="text-sm font-semibold text-zinc-200">{item.name}</p>
                          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-600">{item.qty} x ₹{item.price}</p>
                        </div>
                        <p className="font-mono text-sm text-zinc-100">₹{Math.round(item.qty * item.price)}</p>
                      </div>
                    )) : (
                      <p className="text-sm text-zinc-600">No items added yet.</p>
                    )}
                  </div>
                  {!!manualLines.length && (
                    <div className="mt-4 space-y-2 border-t border-zinc-900 pt-3">
                      {manualLines.filter((line) => line.label && Number(line.amount || 0) !== 0).map((line) => (
                        <div key={line.id} className="flex justify-between gap-3 font-mono text-xs text-zinc-400">
                          <span>{line.label}</span>
                          <span>{Number(line.amount || 0) < 0 ? "-" : ""}₹{Math.abs(Math.round(Number(line.amount || 0)))}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {!!taxPreview.length && (
                    <div className="mt-4 space-y-2 border-t border-zinc-900 pt-3">
                      {taxPreview.map((rule) => (
                        <div key={rule.key || rule.label} className="flex justify-between gap-3 font-mono text-xs text-orange-300">
                          <span>{rule.label} {rule.type !== "fixed" ? `${rule.rate}%` : ""}</span>
                          <span>₹{Math.round(rule.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-5 flex items-end justify-between border-t border-zinc-800 pt-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">Total</span>
                    <span className="font-display text-4xl font-semibold tracking-tight text-orange-500">₹{formatMoney(grandTotal)}</span>
                  </div>
                </div>
                <div className="mt-6 grid gap-2">
                  <button disabled={busy || storageLocked} onClick={() => submitDraft("counter")} className="flex items-center justify-center gap-2 bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
                    <IndianRupee className="h-4 w-4" /> {draft.payment_mode === "credit" ? "Save Credit Sale" : "Create Counter Bill"}
                  </button>
                  <button disabled={busy || storageLocked || !routeMerchantActive} onClick={() => submitDraft("razorpay")} className="flex items-center justify-center gap-2 border border-emerald-500 py-3 font-display font-black uppercase tracking-widest text-emerald-300 hover:bg-emerald-500 hover:text-black disabled:opacity-50">
                    <IndianRupee className="h-4 w-4" /> Collect by {gatewayLabel}
                  </button>
                  <button disabled={busy || storageLocked || !routeMerchantActive} onClick={() => submitDraft("link")} className="flex items-center justify-center gap-2 border border-orange-500 py-3 font-display font-black uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-50">
                    <Send className="h-4 w-4" /> Send {gatewayLabel} Link
                  </button>
                  {storageLocked && <p className="text-center text-xs text-red-300">{trialLocked ? "Your 99 free Mini transactions are complete. Subscribe to continue billing." : "Mini storage is full. Archive old records or upgrade storage."}</p>}
                  {!routeMerchantActive && (
                    <p className="text-center text-xs text-orange-200/80">Online billing unlocks after activating a gateway in Merchant profile.</p>
                  )}
                </div>
                </div>
              </div>
            </div>
          )}

          {tab === "billing" && (
            <div className="space-y-5 p-4 md:hidden">
              <BillAdjustmentConfig
                manualLines={manualLines}
                addManualLine={addManualLine}
                updateManualLine={updateManualLine}
                removeManualLine={removeManualLine}
              />
            </div>
          )}

          {tab === "history" && (
            <div className="space-y-4 p-5">
              <ExportButtons onExport={exportCsv} />
              <LedgerFilters filters={filters} upd={updFilter} onSearch={load} busy={busy} />
              <BulkTransactionBar count={selectedBills.filter((id) => visibleBillIds.includes(id)).length} visibleCount={visibleBillIds.length} busy={busy} onSelectVisible={toggleSelectVisibleBills} onArchive={archiveSelectedBills} />
              <LedgerTable rows={historyRows} empty="No transactions in this filter." onRefund={refundRow} onCustomer={openCustomerLedger} onResend={resendInvoice} onRetryPayment={retryPaymentLink} onCollectOffline={collectOfflineBill} onPrint={setReceiptRow} onEdit={openEditBill} onCancel={cancelBill} selectedIds={selectedBills} selectableIds={visibleBillIds} onToggleSelect={toggleSelectedBill} onToggleSelectVisible={toggleSelectVisibleBills} />
            </div>
          )}

          {tab === "refunds" && (
            <div className="space-y-4 p-5">
              <ExportButtons onExport={exportCsv} />
              <LedgerFilters filters={filters} upd={updFilter} onSearch={load} busy={busy} />
              <RefundStatusTable rows={ledgerRefunds.length ? ledgerRefunds : refunds} />
            </div>
          )}

          {tab === "failed" && (
            <FailedPaymentsPanel rows={paymentIssues} busy={busy} onRetry={retryPaymentLink} onCollectOffline={collectOfflineBill} onSync={syncPaymentStatus} />
          )}

          {tab === "settlements" && (
            <SettlementDashboard rows={settlements} />
          )}

          {tab === "credits" && (
            <CreditSaleNotes
              draft={creditDraft}
              updateDraft={updateCreditDraft}
              items={creditItems}
              updateItem={updateCreditItem}
              addItem={addCreditItem}
              removeItem={removeCreditItem}
              total={creditTotal}
              credits={credits}
              busy={busy}
              onSave={createCredit}
              onPay={payCredit}
              onPaymentLink={sendCreditPaymentLink}
              paymentDrafts={creditPayments}
              updatePaymentDraft={updateCreditPayment}
              onCustomer={openCustomerLedger}
              issued={creditIssued}
              settled={creditSettled}
              pending={creditPending}
            />
          )}

          {tab === "profile" && (
            <MerchantProfilePanel
              subscription={subscription}
              storageUsage={storageUsage}
              storageArchives={storageArchives}
              storageDraft={storageDraft}
              setStorageDraft={setStorageDraft}
              busy={busy}
              selectedArchiveCount={selectedBills.filter((id) => visibleBillIds.includes(id)).length}
              onArchiveSelected={archiveSelectedBills}
              onDownloadArchive={downloadArchive}
              onViewArchive={setArchiveViewer}
              onSaveStorageSettings={saveStorageSettings}
              onRunCleanup={runAutoCleanup}
              merchants={routeMerchants}
              savedGateways={savedGateways}
              routeDraft={routeDraft}
              setRouteDraft={setRouteDraft}
              onSaveRouteMerchant={saveRouteMerchant}
              onActivateGateway={activateSavedGateway}
              onRetryRouteMerchant={retryRouteMerchant}
              onRefreshGateways={loadRouteMerchants}
              settings={settings}
              setSettings={setSettings}
            />
          )}
        </section>
      </main>
      <FloatingStickyNotes
        open={stickyOpen}
        setOpen={setStickyOpen}
        pages={stickyPages}
        pageIndex={stickyIndex}
        setPageIndex={setStickyIndex}
        updatePage={updateStickyPage}
        addPage={addStickyPage}
        removePage={removeStickyPage}
        erasePage={eraseStickyPage}
        deleteAll={deleteAllStickyPages}
        toggleLock={toggleStickyLock}
      />
      <CustomerLedgerDrawer state={customerLedger} onClose={() => setCustomerLedger((current) => ({ ...current, open: false }))} />
      <ReceiptDrawer row={receiptRow} onClose={() => setReceiptRow(null)} />
      <EditBillModal bill={editBill} setBill={setEditBill} onSave={saveEditedBill} busy={busy} />
      <ArchiveViewer archive={archiveViewer} onClose={() => setArchiveViewer(null)} onDownload={downloadArchive} />
    </div>
  );
}

function MerchantProfilePanel({
  subscription,
  storageUsage,
  storageArchives,
  storageDraft,
  setStorageDraft,
  busy,
  selectedArchiveCount,
  onArchiveSelected,
  onDownloadArchive,
  onViewArchive,
  onSaveStorageSettings,
  onRunCleanup,
  merchants,
  savedGateways,
  routeDraft,
  setRouteDraft,
  onSaveRouteMerchant,
  onActivateGateway,
  onRetryRouteMerchant,
  onRefreshGateways,
  settings,
  setSettings,
}) {
  const [profileTab, setProfileTab] = useState("overview");
  return (
    <div className="space-y-6 p-5">
      <section className="border border-zinc-800 bg-zinc-950 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Merchant profile</p>
            <h2 className="font-display text-3xl font-black tracking-tight">Account, billing, payments</h2>
            <p className="mt-2 max-w-3xl text-sm text-zinc-500">Manage Mini subscription visibility, storage cleanup, payment gateway activation, and invoice identity from one place.</p>
          </div>
          <span className="border border-orange-500/40 bg-orange-500/10 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300">
            {subscription?.plan === "terminal_ledger" ? "Mini" : subscription?.plan || "No plan"}
          </span>
        </div>
      </section>

      <div className="flex gap-2 overflow-x-auto border border-zinc-800 bg-black p-2">
        <MerchantProfileTab active={profileTab === "overview"} onClick={() => setProfileTab("overview")}>Overview</MerchantProfileTab>
        <MerchantProfileTab active={profileTab === "invoice"} onClick={() => setProfileTab("invoice")}>Invoice / GST</MerchantProfileTab>
        <MerchantProfileTab active={profileTab === "storage"} onClick={() => setProfileTab("storage")}>Storage</MerchantProfileTab>
        <MerchantProfileTab active={profileTab === "payments"} onClick={() => setProfileTab("payments")}>Payments</MerchantProfileTab>
      </div>

      {profileTab === "overview" && (
        <div className="grid gap-5 xl:grid-cols-[1fr_1fr]">
          <MiniTrialMeter subscription={subscription} />
          <StorageMeter usage={storageUsage} />
        </div>
      )}

      {profileTab === "invoice" && <MiniBillingSettings settings={settings} setSettings={setSettings} />}

      {profileTab === "storage" && (
        <StorageManager
          usage={storageUsage}
          archives={storageArchives}
          draft={storageDraft}
          setDraft={setStorageDraft}
          busy={busy}
          selectedCount={selectedArchiveCount}
          onArchiveSelected={onArchiveSelected}
          onDownloadArchive={onDownloadArchive}
          onViewArchive={onViewArchive}
          onSaveSettings={onSaveStorageSettings}
          onRunCleanup={onRunCleanup}
        />
      )}

      {profileTab === "payments" && (
        <PaymentSetupPanel
          merchants={merchants}
          savedGateways={savedGateways}
          draft={routeDraft}
          setDraft={setRouteDraft}
          busy={busy}
          onSave={onSaveRouteMerchant}
          onActivate={onActivateGateway}
          onRetry={onRetryRouteMerchant}
          onRefresh={onRefreshGateways}
        />
      )}
    </div>
  );
}

function MerchantProfileTab({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} className={`shrink-0 px-4 py-3 font-mono text-[10px] uppercase tracking-widest ${active ? "bg-orange-500 text-black" : "text-zinc-400 hover:text-orange-500"}`}>
      {children}
    </button>
  );
}

function InvoiceIdentityPreview({ settings = {} }) {
  const name = settings.shop_name || settings.legal_name || "Onboarding shop profile";
  const location = [settings.city, settings.pincode].filter(Boolean).join(" - ");
  return (
    <section className="border border-zinc-800 bg-zinc-950 p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Invoice preview</p>
          <h3 className="mt-1 font-display text-xl font-semibold text-zinc-100">{name}</h3>
          <p className="mt-1 text-xs text-zinc-500">Read-only shop identity from onboarding. Full edits stay managed by platform onboarding.</p>
        </div>
        <span className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
          {settings.invoice_prefix || "MINI"} invoice
        </span>
      </div>
      <div className="mt-4 grid gap-3 text-xs sm:grid-cols-4">
        {settings.gstin && <InfoLine label="GSTIN" value={settings.gstin} />}
        <InfoLine label="Phone" value={settings.phone || "Not set"} />
        <InfoLine label="Email" value={settings.email || "Not set"} />
        <InfoLine label="Location" value={location || "Not set"} />
      </div>
    </section>
  );
}

function BillAdjustmentConfig({
  manualLines,
  addManualLine,
  updateManualLine,
  removeManualLine,
}) {
  return (
    <div className="border border-zinc-800 bg-zinc-950/70">
      <div className="flex items-center justify-between gap-3 border-b border-zinc-800 px-4 py-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-orange-500">Adjustments</p>
          <p className="mt-1 text-xs text-zinc-500">Add packing, delivery, discount, round-off, or any custom line.</p>
        </div>
        <button type="button" onClick={addManualLine} className="inline-flex items-center gap-2 border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
          <Plus className="h-3.5 w-3.5" /> Add line
        </button>
      </div>
      <div className="divide-y divide-zinc-800">
        {manualLines.map((line) => (
          <div key={line.id} className="grid gap-3 p-4 sm:grid-cols-[1fr_130px_42px] sm:items-end">
            <LedgerField label="Label" value={line.label} onChange={(v) => updateManualLine(line.id, "label", v)} />
            <LedgerField label="Amount +/-" type="number" value={line.amount} onChange={(v) => updateManualLine(line.id, "amount", v)} />
            <button type="button" onClick={() => removeManualLine(line.id)} className="h-[46px] border border-zinc-800 text-zinc-500 hover:border-red-400 hover:text-red-300" aria-label="Remove adjustment">
              <Trash2 className="mx-auto h-4 w-4" />
            </button>
          </div>
        ))}
        {!manualLines.length && <p className="px-4 py-5 text-sm text-zinc-600">No custom adjustment lines.</p>}
      </div>
    </div>
  );
}

function PaymentSetupPanel({ merchants, savedGateways = [], draft, setDraft, busy, onSave, onActivate, onRetry, onRefresh }) {
  const active = merchants[0];
  const update = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const status = active?.active ? "ACTIVE" : "NOT CONFIGURED";
  const provider = active?.provider || draft.provider || "razorpay";
  const label = active?.provider_label || providerLabel(provider);
  const selectedProvider = draft.provider || provider;
  const fieldConfig = providerFieldConfig[selectedProvider] || providerFieldConfig.razorpay;
  const docs = gatewayDocs[draft.provider || provider] || gatewayDocs.razorpay;
  const statusTone = active?.active ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-orange-300 border-orange-500/40 bg-orange-500/10";

  return (
    <div className="space-y-5 p-5">
      <div className="grid gap-5 lg:grid-cols-[1fr_380px]">
        <div className="border border-zinc-800 bg-zinc-950 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Online payment activation</p>
              <h3 className="font-display text-2xl font-semibold tracking-tight">Merchant-owned gateway</h3>
              <p className="mt-1 max-w-2xl text-sm text-zinc-500">Choose the merchant gateway and save that merchant's API credentials. Terminal only uses those credentials for Mini billing.</p>
            </div>
            <button type="button" onClick={onRefresh} disabled={busy} className="inline-flex items-center gap-2 border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500 disabled:opacity-50">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            {["razorpay"].map((option) => (
              <button key={option} type="button" onClick={() => update("provider", option)} className={`border px-4 py-4 text-left ${draft.provider === option ? "border-orange-500 bg-orange-500/10 text-orange-200" : "border-zinc-800 bg-black text-zinc-400 hover:border-orange-500/60"}`}>
                <p className="font-display text-lg font-semibold">{providerLabel(option)}</p>
                <p className="mt-1 text-xs text-zinc-500">{savedGateways.some((gateway) => gateway.provider === option) ? "Saved. Select to edit credentials." : `Use merchant's own ${providerLabel(option)} account`}</p>
              </button>
            ))}
          </div>

          {!!savedGateways.length && (
            <div className="mt-5 border border-zinc-800 bg-black p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Saved gateways</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                {savedGateways.map((gateway) => (
                  <div key={gateway.provider} className={`border p-3 ${gateway.active ? "border-emerald-500/50 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-display font-semibold text-zinc-100">{gateway.provider_label || providerLabel(gateway.provider)}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{gateway.mode || "test"} · {gateway.masked_key_id || "saved"}</p>
                      </div>
                      <span className={`border px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${gateway.active ? "border-emerald-500/40 text-emerald-300" : "border-zinc-700 text-zinc-500"}`}>{gateway.active ? "Active" : "Saved"}</span>
                    </div>
                    {!gateway.active && (
                      <button type="button" disabled={busy} onClick={() => onActivate?.(gateway.provider)} className="mt-3 w-full border border-orange-500/50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-50">
                        Activate
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Mode</span>
              <select value={draft.mode} onChange={(e) => update("mode", e.target.value)} className="mt-2 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500">
                <option value="test">Test</option>
                <option value="live">Live</option>
              </select>
            </label>
            <LedgerField label={fieldConfig.keyLabel} value={draft.key_id} onChange={(v) => update("key_id", v)} />
            <LedgerField label={fieldConfig.secretLabel} value={draft.key_secret} onChange={(v) => update("key_secret", v)} />
            <LedgerField label={fieldConfig.webhookLabel} value={draft.webhook_secret} onChange={(v) => update("webhook_secret", v)} />
            {(fieldConfig.extra || []).map((field) => (
              <LedgerField key={field.key} label={field.label} value={draft[field.key] || ""} placeholder={field.placeholder} onChange={(v) => update(field.key, v)} />
            ))}
          </div>
          <button disabled={busy} onClick={onSave} className="mt-5 inline-flex items-center gap-2 bg-orange-500 px-5 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
            <IndianRupee className="h-4 w-4" /> {busy ? "Saving..." : `Activate ${providerLabel(draft.provider)}`}
          </button>
        </div>

        <div className="space-y-4">
          <div className={`border p-5 ${statusTone}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em]">Current status</p>
            <p className="mt-3 font-display text-3xl font-black tracking-tight">{status}</p>
            <p className="mt-2 text-sm opacity-80">
              {active?.active ? `${label} is active for Mini online billing.` : "Counter collect and payment links stay disabled until a gateway is activated."}
            </p>
          </div>
          <div className="border border-zinc-800 bg-zinc-950 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Active gateway</p>
            {active ? (
              <div className="mt-4 space-y-3 text-sm">
                <InfoLine label="Provider" value={active.provider_label} />
                <InfoLine label="Mode" value={active.mode} />
                <InfoLine label="Key" value={active.masked_key_id} />
                <InfoLine label="Secret" value={active.has_key_secret ? "Saved" : "Missing"} />
                <InfoLine label="Webhook" value={active.has_webhook_secret ? "Saved" : "Optional / missing"} />
              </div>
            ) : (
              <p className="mt-4 text-sm text-zinc-500">No gateway activated yet.</p>
            )}
          </div>
          <div className="border border-zinc-800 bg-black p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Manual setup guide</p>
            <div className="mt-4 space-y-3 text-sm text-zinc-400">
              {docs.map((line, index) => <p key={index}>{line}</p>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <div className="flex justify-between gap-3 border-b border-zinc-900 pb-2 last:border-b-0">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <span className="max-w-[210px] truncate text-right text-zinc-200">{value || "-"}</span>
    </div>
  );
}

function MiniBillingSettings({ settings, setSettings }) {
  const [draft, setDraft] = useState(normalizeSettings(settings));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(normalizeSettings(settings)); }, [settings]);
  const hasDraftGstin = Boolean(String(draft.gstin || "").trim());
  const update = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const updateRule = (idx, patch) => setDraft((current) => ({ ...current, rules: current.rules.map((rule, i) => i === idx ? { ...rule, ...patch } : rule) }));
  const addRule = () => setDraft((current) => ({ ...current, rules: [...current.rules, { key: `line-${Date.now()}`, label: "New tax/charge", type: "percentage", rate: 0, scope: "shop", active: true }] }));
  const removeRule = (idx) => setDraft((current) => ({ ...current, rules: current.rules.filter((_, i) => i !== idx) }));
  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put("/admin/billing-settings", draft);
      setSettings(normalizeSettings(res.data || draft));
      toast.success("Mini GST invoice settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save billing settings");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 p-5">
      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <div className="border border-zinc-800 bg-zinc-950 p-5">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-orange-500" />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Invoice identity</p>
              <h3 className="font-display text-2xl font-semibold tracking-tight">Shop GST profile</h3>
            </div>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <LedgerField label="Shop display name" value={draft.shop_name || ""} onChange={(v) => update("shop_name", v)} />
            <LedgerField label="Legal name" value={draft.legal_name || ""} onChange={(v) => update("legal_name", v)} />
            <div>
              <LedgerField label="GSTIN (optional)" value={draft.gstin || ""} onChange={(v) => update("gstin", v.toUpperCase())} />
              {draft.gstin && (
                <button type="button" onClick={() => update("gstin", "")} className="mt-2 border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:border-red-400 hover:text-red-300">
                  Remove GSTIN
                </button>
              )}
            </div>
            <LedgerField label="Invoice prefix" value={draft.invoice_prefix || ""} onChange={(v) => update("invoice_prefix", v.toUpperCase())} />
            <LedgerField label="Phone" value={draft.phone || ""} onChange={(v) => update("phone", v)} />
            <LedgerField label="Email" type="email" value={draft.email || ""} onChange={(v) => update("email", v.toLowerCase())} />
            <LedgerField label="City" value={draft.city || ""} onChange={(v) => update("city", v)} />
            <LedgerField label="Pincode" value={draft.pincode || ""} onChange={(v) => update("pincode", v.replace(/\D/g, "").slice(0, 6))} />
          </div>
          <label className="mt-3 block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Billing address</span>
            <textarea value={draft.billing_address || ""} onChange={(e) => update("billing_address", e.target.value)} className="mt-2 min-h-24 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none placeholder:text-zinc-700 focus:border-orange-500" placeholder="Full shop address for invoice" />
          </label>
        </div>

        <div className="border border-zinc-800 bg-gradient-to-b from-zinc-950 to-black p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Preview</p>
          <div className="mt-4 border border-zinc-800 bg-zinc-950 p-4">
            <p className="font-display text-xl font-semibold">{draft.shop_name || "Your shop name"}</p>
            <p className="mt-1 text-sm text-zinc-400">{draft.legal_name || "Legal name"}</p>
            {draft.gstin && <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">GSTIN {draft.gstin}</p>}
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Invoice {draft.invoice_prefix || "MINI"}-2026-00001</p>
            <p className="mt-4 text-xs leading-relaxed text-zinc-500">{draft.billing_address || "Billing address will appear here."}</p>
          </div>
        </div>
      </div>

      <div className="border border-zinc-800 bg-zinc-950 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Taxes and charges</p>
            <h3 className="font-display text-2xl font-semibold tracking-tight">Dynamic GST rules</h3>
            <p className="mt-1 text-sm text-zinc-500">{hasDraftGstin ? "Use CGST/SGST, IGST, packing charge, platform fee, or any future tax line." : "Add GSTIN above to enable GST and tax rule setup."}</p>
          </div>
          <button type="button" disabled={!hasDraftGstin} onClick={addRule} className="inline-flex items-center gap-2 border border-orange-500 px-4 py-2 font-mono text-xs uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:cursor-not-allowed disabled:opacity-40">
            <Plus className="h-4 w-4" /> Add rule
          </button>
        </div>
        <div className="mt-4 space-y-2">
          {!hasDraftGstin && (
            <div className="border border-zinc-800 bg-black p-4 text-sm text-zinc-500">
              GST rules are disabled. New bills and invoices will not include GST details until a GSTIN is saved.
            </div>
          )}
          {(draft.rules || []).map((rule, idx) => (
            <div key={`${rule.key}-${idx}`} className="grid gap-2 border border-zinc-800 bg-black p-3 md:grid-cols-[1fr_130px_110px_120px_90px_42px]">
              <input disabled={!hasDraftGstin} value={rule.label || ""} onChange={(e) => updateRule(idx, { label: e.target.value, key: (rule.key || e.target.value).toString().toLowerCase().replace(/\s+/g, "-") })} placeholder="CGST" className="border border-zinc-800 bg-zinc-950 px-3 py-2 font-mono text-xs outline-none focus:border-orange-500 disabled:opacity-50" />
              <select disabled={!hasDraftGstin} value={rule.type || "percentage"} onChange={(e) => updateRule(idx, { type: e.target.value })} className="border border-zinc-800 bg-zinc-950 px-2 py-2 font-mono text-xs outline-none focus:border-orange-500 disabled:opacity-50">
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed</option>
              </select>
              <input disabled={!hasDraftGstin} type="number" step="0.01" value={rule.rate ?? 0} onChange={(e) => updateRule(idx, { rate: Number(e.target.value) })} className="border border-zinc-800 bg-zinc-950 px-3 py-2 font-mono text-xs outline-none focus:border-orange-500 disabled:opacity-50" />
              <select disabled={!hasDraftGstin} value={rule.scope || "shop"} onChange={(e) => updateRule(idx, { scope: e.target.value })} className="border border-zinc-800 bg-zinc-950 px-2 py-2 font-mono text-xs outline-none focus:border-orange-500 disabled:opacity-50">
                <option value="shop">Mini/shop</option>
                <option value="service">Service</option>
                <option value="both">Both</option>
              </select>
              <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                <input type="checkbox" disabled={!hasDraftGstin} checked={rule.active !== false} onChange={(e) => updateRule(idx, { active: e.target.checked })} /> Active
              </label>
              <button type="button" disabled={!hasDraftGstin} onClick={() => removeRule(idx)} className="text-red-400 hover:bg-red-500/10 disabled:opacity-40">X</button>
            </div>
          ))}
        </div>
        <button disabled={busy} onClick={save} className="mt-5 inline-flex items-center gap-2 bg-orange-500 px-5 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
          <Settings className="h-4 w-4" /> {busy ? "Saving..." : "Save GST invoice setup"}
        </button>
      </div>
    </div>
  );
}

function normalizeSettings(data = {}) {
  return {
    ...data,
    rules: Array.isArray(data.rules) && data.rules.length ? data.rules : defaultTaxRules,
  };
}

function MiniBars({ transactions, credits }) {
  const paid = transactions.filter((row) => row.kind !== "refund").reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const refunds = transactions.filter((row) => row.kind === "refund").reduce((sum, row) => sum + Math.abs(Number(row.amount || 0)), 0);
  const pending = credits.reduce((sum, row) => sum + Number(row.balance_amount || 0), 0);
  const max = Math.max(paid, refunds, pending, 1);
  const rows = [
    ["Collected", paid, "bg-orange-500"],
    ["Refunds", refunds, "bg-red-400"],
    ["Credit due", pending, "bg-sky-400"],
  ];
  return (
    <section className="border border-zinc-800 bg-zinc-950 p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">Mini pulse</p>
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {rows.map(([label, value, color]) => (
          <div key={label}>
            <div className="mb-2 flex justify-between font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              <span>{label}</span><span>₹{Math.round(value)}</span>
            </div>
            <div className="h-2 bg-zinc-900">
              <div className={`h-full ${color}`} style={{ width: `${Math.max(4, Math.round((value / max) * 100))}%` }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FailedPaymentsPanel({ rows, busy, onRetry, onCollectOffline, onSync }) {
  const failed = rows.filter((row) => String(row.status || "").toUpperCase() === "PAYMENT_FAILED");
  const pending = rows.filter((row) => String(row.status || "").toUpperCase() !== "PAYMENT_FAILED");
  return (
    <div className="space-y-5 p-5">
      <div className="grid gap-px bg-zinc-800 md:grid-cols-3">
        <LedgerMetric icon={RefreshCw} label="Failed links" value={failed.length} sub="needs retry or customer follow-up" />
        <LedgerMetric icon={IndianRupee} label="Pending links" value={pending.length} sub="waiting for customer payment" />
        <LedgerMetric icon={IndianRupee} label="Pending value" value={`₹${Math.round(rows.reduce((sum, row) => sum + Number(row.total || 0), 0))}`} sub="unpaid Razorpay link value" />
      </div>
      <div className="overflow-x-auto border border-zinc-800">
        <table className="min-w-full divide-y divide-zinc-800 text-left">
          <thead className="bg-zinc-950">
            <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <th className="px-4 py-3">Bill</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {rows.map((row) => (
              <tr key={row.id} className="bg-zinc-950/40 text-sm text-zinc-300">
                <td className="px-4 py-3">
                  <p className="font-display font-bold text-zinc-100">{row.invoice_number || row.title || row.id}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{formatTs(row.payment_failed_at || row.payment_retry_at || row.updated_at || row.created_at)}</p>
                </td>
                <td className="px-4 py-3">
                  <p>{row.customer_name || "Walk-in"}</p>
                  <p className="font-mono text-[10px] text-zinc-500">{row.customer_phone || row.customer_email || "No contact"}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${String(row.status || "").includes("FAILED") ? "border-red-500/50 text-red-300" : "border-orange-500/50 text-orange-300"}`}>
                    {row.status || "-"}
                  </span>
                  <p className="mt-1 font-mono text-[10px] text-zinc-600">Retries {row.payment_retry_count || 0}</p>
                </td>
                <td className="px-4 py-3 text-right font-display text-lg font-black text-orange-500">₹{formatMoney(row.total)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex flex-wrap justify-end gap-2">
                    {row.payment_link && (
                      <a href={row.payment_link} target="_blank" rel="noreferrer" className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                        Open
                      </a>
                    )}
                    <button disabled={busy} onClick={() => onRetry(row)} className="border border-emerald-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300 hover:bg-emerald-500 hover:text-black disabled:opacity-50">
                      Resend link
                    </button>
                    <button disabled={busy} onClick={() => onSync?.(row)} className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500 disabled:opacity-50">
                      Sync status
                    </button>
                    <button disabled={busy} onClick={() => onCollectOffline?.(row)} className="border border-sky-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-sky-300 hover:bg-sky-500 hover:text-black disabled:opacity-50">
                      Paid offline
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-zinc-500">No failed or pending payment links.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RefundStatusTable({ rows }) {
  return (
    <div className="overflow-x-auto border border-zinc-800">
      <table className="min-w-full divide-y divide-zinc-800 text-left">
        <thead className="bg-zinc-950">
          <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            <th className="px-4 py-3">Refund</th>
            <th className="px-4 py-3">Customer</th>
            <th className="px-4 py-3">Razorpay</th>
            <th className="px-4 py-3 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-800">
          {rows.map((row) => {
            const status = row.razorpay_refund_status || row.status || row.extra?.status || "LOCAL";
            return (
              <tr key={row.id} className="bg-zinc-950/40 text-sm text-zinc-300">
                <td className="px-4 py-3">
                  <p className="font-display font-bold text-zinc-100">{row.reason || row.title || "Refund"}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{formatTs(row.refund_synced_at || row.decided_at || row.ts || row.created_at)}</p>
                </td>
                <td className="px-4 py-3">
                  <p>{row.customer_name || "Customer"}</p>
                  <p className="font-mono text-[10px] text-zinc-500">{row.customer_phone || "No phone"}</p>
                </td>
                <td className="px-4 py-3">
                  <span className="border border-orange-500/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-orange-300">{status}</span>
                  <p className="mt-1 font-mono text-[10px] text-zinc-600">{row.razorpay_refund_id || row.razorpay_refund_error || "Local refund"}</p>
                </td>
                <td className="px-4 py-3 text-right font-display text-lg font-black text-red-400">₹{Math.round(Math.abs(Number(row.amount || 0)))}</td>
              </tr>
            );
          })}
          {!rows.length && (
            <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-zinc-500">No Mini refunds yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SettlementDashboard({ rows }) {
  const total = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const fees = rows.reduce((sum, row) => sum + Number(row.fees || 0), 0);
  const tax = rows.reduce((sum, row) => sum + Number(row.tax || 0), 0);
  return (
    <div className="space-y-5 p-5">
      <div className="grid gap-px bg-zinc-800 md:grid-cols-3">
        <LedgerMetric icon={IndianRupee} label="Settled" value={`₹${Math.round(total)}`} sub={`${rows.length} Razorpay settlements`} />
        <LedgerMetric icon={IndianRupee} label="Fees" value={`₹${Math.round(fees)}`} sub="gateway fees reported" />
        <LedgerMetric icon={IndianRupee} label="Tax" value={`₹${Math.round(tax)}`} sub="tax on fees" />
      </div>
      <div className="overflow-x-auto border border-zinc-800">
        <table className="min-w-full divide-y divide-zinc-800 text-left">
          <thead className="bg-zinc-950">
            <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <th className="px-4 py-3">Settlement</th>
              <th className="px-4 py-3">UTR</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {rows.map((row) => (
              <tr key={row.id || row.razorpay_settlement_id} className="bg-zinc-950/40 text-sm text-zinc-300">
                <td className="px-4 py-3">
                  <p className="font-display font-bold text-zinc-100">{row.razorpay_settlement_id || row.id}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{formatTs(row.settled_at || row.updated_at)}</p>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{row.utr || "-"}</td>
                <td className="px-4 py-3"><span className="border border-emerald-500/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-300">{row.status || "-"}</span></td>
                <td className="px-4 py-3 text-right">
                  <p className="font-display text-lg font-black text-orange-500">₹{Math.round(Number(row.amount || 0))}</p>
                  <p className="font-mono text-[10px] text-zinc-600">fees ₹{Math.round(Number(row.fees || 0))} · tax ₹{Math.round(Number(row.tax || 0))}</p>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-zinc-500">No settlement webhooks received yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExportButtons({ onExport }) {
  return (
    <div className="flex flex-wrap gap-2">
      {["transactions", "credits", "refunds"].map((type) => (
        <button key={type} onClick={() => onExport(type)} className="inline-flex items-center gap-2 border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
          <Download className="h-3.5 w-3.5" /> Export {type}
        </button>
      ))}
    </div>
  );
}

function FloatingStickyNotes({ open, setOpen, pages, pageIndex, setPageIndex, updatePage, addPage, removePage, erasePage, deleteAll, toggleLock }) {
  const page = pages[pageIndex] || pages[0] || emptyStickyPage();
  const [pinPanel, setPinPanel] = useState({ mode: "", pin: "" });
  const colorClass = stickyColors[page.color] || stickyColors.amber;
  const stamp = page.createdAt ? new Date(page.createdAt).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }) : "";
  useEffect(() => { setPinPanel({ mode: "", pin: "" }); }, [pageIndex, open]);
  const clearPinPanel = () => setPinPanel({ mode: "", pin: "" });
  const askPin = (mode) => setPinPanel({ mode, pin: "" });
  const lockedMessage = () => toast.error(page.has_pin ? "Enter PIN on this page first" : "This page is locked. Unlock it first.");
  const lockNormally = () => toggleLock({ pinLock: false });
  const submitPin = async () => {
    if (!/^\d{4}$/.test(pinPanel.pin)) return toast.error("Enter a valid 4 digit PIN");
    if (pinPanel.mode === "lock-pin") {
      const ok = await toggleLock({ pinLock: true, pin: pinPanel.pin });
      if (ok) clearPinPanel();
      return;
    }
    const ok = await toggleLock({ pin: pinPanel.pin });
    if (!ok) return;
    const mode = pinPanel.mode;
    clearPinPanel();
    if (mode === "erase") window.setTimeout(erasePage, 0);
    if (mode === "remove") window.setTimeout(removePage, 0);
  };
  const guardedErase = () => {
    if (!page.locked) return erasePage();
    if (page.has_pin) return askPin("erase");
    return lockedMessage();
  };
  const guardedRemove = () => {
    if (!page.locked) return removePage();
    if (page.has_pin) return askPin("remove");
    return lockedMessage();
  };
  const guardedDeleteAll = () => {
    if (pages.some((item) => item.locked)) return toast.error("Unlock locked sticky pages before deleting all notes.");
    return deleteAll();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-orange-500 text-black shadow-2xl shadow-orange-950/50 ring-1 ring-orange-300/40 transition duration-300 hover:-translate-y-1 hover:scale-105 hover:bg-orange-400"
        aria-label="Open sticky notes"
      >
        <StickyNote className="h-5 w-5" />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/20 p-4 backdrop-blur-[1px]">
          <div className="mb-14 max-h-[76vh] w-[min(360px,calc(100vw-2rem))] origin-bottom-right overflow-hidden border border-zinc-800 bg-zinc-950 shadow-2xl shadow-black/60 transition duration-300 ease-out animate-in fade-in zoom-in-95 slide-in-from-bottom-4">
            <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-2">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-orange-500">Desk sticky notes</p>
                <p className="text-xs text-zinc-500">Page {pageIndex + 1} of {pages.length}</p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={addPage} className="border border-zinc-800 p-1.5 text-zinc-300 hover:border-orange-500 hover:text-orange-500" aria-label="Add page">
                  <FilePlus2 className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => setOpen(false)} className="border border-zinc-800 p-1.5 text-zinc-300 hover:border-orange-500 hover:text-orange-500" aria-label="Close sticky notes">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto border-b border-zinc-800 px-3 py-2">
              {pages.map((item, index) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => setPageIndex(index)}
                  className={`min-w-8 border px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest ${index === pageIndex ? "border-orange-500 bg-orange-500 text-black" : "border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-400"}`}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            <div className="max-h-[calc(76vh-92px)] overflow-y-auto p-3">
              <div className="relative mx-auto max-w-[300px]">
                {pages.length > 1 && (
                  <>
                    <div className="absolute inset-0 rotate-3 border border-black/10 bg-white/20 shadow-lg" />
                    <div className="absolute inset-0 -rotate-2 border border-black/10 bg-white/10 shadow-lg" />
                  </>
                )}
                <div className={`relative min-h-[250px] border bg-gradient-to-br p-3 shadow-xl transition duration-300 ${colorClass}`}>
                <div className="absolute right-3 top-3 max-w-[92px] text-right font-mono text-[9px] uppercase tracking-[0.08em] text-black/45">
                  {stamp || "Date starts on first write"}
                </div>
                <input
                  value={page.locked ? "" : (page.title || "")}
                  disabled={page.locked}
                  onChange={(e) => updatePage({ title: e.target.value })}
                  placeholder={page.locked ? "Locked page" : "Heading or title"}
                  className="mr-24 w-[calc(100%-6rem)] bg-transparent font-display text-lg font-black tracking-tight outline-none placeholder:text-black/35 disabled:cursor-not-allowed"
                />
                <textarea
                  value={page.locked ? "" : (page.text || "")}
                  disabled={page.locked}
                  onChange={(e) => updatePage({ text: e.target.value })}
                  placeholder={page.locked ? "Enter PIN to unlock this note." : "Start writing..."}
                  className="mt-4 min-h-[170px] w-full resize-none bg-transparent text-sm leading-6 outline-none placeholder:text-black/35 disabled:cursor-not-allowed"
                />
                {page.locked && (
                  <div className="absolute inset-x-3 bottom-3 border border-black/10 bg-black/10 px-2 py-1.5 font-mono text-[9px] uppercase tracking-widest text-black/60 backdrop-blur-sm">
                    {page.has_pin ? "Page locked with PIN" : "Page locked"}
                  </div>
                )}
                </div>
              </div>

              {pinPanel.mode && (
                <div className="mt-3 border border-orange-500/40 bg-orange-500/10 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-orange-300">
                        {pinPanel.mode === "lock-pin" ? "Set page PIN" : "Enter page PIN"}
                      </p>
                      <p className="mt-1 text-xs text-zinc-500">
                        {pinPanel.mode === "lock-pin" ? "Use exactly 4 digits. The PIN is stored hashed." : "This action needs the locked page PIN."}
                      </p>
                    </div>
                    <button type="button" onClick={clearPinPanel} className="border border-zinc-800 p-1 text-zinc-400 hover:border-orange-500 hover:text-orange-500" aria-label="Close PIN panel">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <input
                      value={pinPanel.pin}
                      onChange={(e) => setPinPanel((current) => ({ ...current, pin: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                      inputMode="numeric"
                      maxLength={4}
                      placeholder="4 digit PIN"
                      className="min-w-0 flex-1 border border-zinc-800 bg-black px-3 py-2 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500"
                    />
                    <button type="button" onClick={submitPin} className="bg-orange-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-black hover:bg-orange-400">
                      Apply
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Color auto-picked per page</p>
                <div className="flex flex-wrap gap-2">
                  {page.locked ? (
                    <button type="button" onClick={() => page.has_pin ? askPin("unlock") : toggleLock()} className="inline-flex items-center gap-1.5 border border-zinc-800 px-2 py-1.5 font-mono text-[9px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                      <Unlock className="h-3.5 w-3.5" /> Unlock
                    </button>
                  ) : (
                    <>
                      <button type="button" onClick={lockNormally} className="inline-flex items-center gap-1.5 border border-zinc-800 px-2 py-1.5 font-mono text-[9px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                        <Lock className="h-3.5 w-3.5" /> Lock
                      </button>
                      <button type="button" onClick={() => askPin("lock-pin")} className="inline-flex items-center gap-1.5 border border-orange-500/60 px-2 py-1.5 font-mono text-[9px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black">
                        <Lock className="h-3.5 w-3.5" /> PIN lock
                      </button>
                    </>
                  )}
                  <button type="button" onClick={guardedErase} className="inline-flex items-center gap-1.5 border border-zinc-800 px-2 py-1.5 font-mono text-[9px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                    <Eraser className="h-3.5 w-3.5" /> Erase
                  </button>
                  <button type="button" onClick={guardedRemove} className="inline-flex items-center gap-1.5 border border-zinc-800 px-2 py-1.5 font-mono text-[9px] uppercase tracking-widest text-red-300 hover:border-red-400">
                    <Trash2 className="h-3.5 w-3.5" /> Remove page
                  </button>
                  <button type="button" onClick={guardedDeleteAll} className="border border-red-500 px-2 py-1.5 font-mono text-[9px] uppercase tracking-widest text-red-300 hover:bg-red-500 hover:text-black">
                    Delete all
                  </button>
                </div>
              </div>
              <p className="mt-2 text-[11px] text-zinc-500">Notes save to your Mini account. Locked pages require the 4 digit PIN.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CustomerLedgerDrawer({ state, onClose }) {
  const data = state.data || {};
  const customer = data.customer || state.seed || {};
  const summary = data.summary || {};
  const timeline = data.timeline || [];
  if (!state.open) return null;
  const saveCustomer = async () => {
    const nextName = window.prompt("Customer name", customer.name || "") || "";
    const nextPhone = window.prompt("Customer phone", customer.phone || "") || "";
    const nextEmail = window.prompt("Customer email", customer.email || "") || "";
    try {
      await api.patch("/ledger/customer", {
        old_name: customer.name,
        old_phone: customer.phone,
        old_email: customer.email,
        customer_name: nextName,
        customer_phone: nextPhone,
        customer_email: nextEmail,
      });
      toast.success("Customer updated");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update customer");
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm">
      <div className="h-full w-full max-w-2xl overflow-y-auto border-l border-zinc-800 bg-zinc-950 shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-zinc-800 bg-zinc-950/95 px-5 py-4 backdrop-blur">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-orange-500">Customer ledger</p>
            <h3 className="mt-1 font-display text-2xl font-black tracking-tight">{customer.name || "Customer"}</h3>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-500">{customer.phone || "No phone"} · {customer.email || "No email"}</p>
          </div>
          <button onClick={onClose} className="border border-zinc-800 p-2 text-zinc-300 hover:border-orange-500 hover:text-orange-500" aria-label="Close customer ledger">
            <X className="h-4 w-4" />
          </button>
        </div>
        {state.loading ? (
          <p className="p-6 text-sm text-zinc-500">Loading customer ledger...</p>
        ) : (
          <div className="space-y-5 p-5">
            <div className="border border-zinc-800 bg-zinc-950 p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">Edit / merge customer identity</p>
              <p className="mt-2 text-xs text-zinc-500">Update name, phone, or email across matching Mini bills, credits, and refunds.</p>
              <button onClick={saveCustomer} className="mt-3 border border-orange-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black">
                Update matching records
              </button>
            </div>
            <div className="grid gap-px bg-zinc-800 sm:grid-cols-3">
              <LedgerMetric icon={IndianRupee} label="Net collected" value={`₹${Math.round(Number(summary.net_collected || 0))}`} sub="paid minus refunds" />
              <LedgerMetric icon={Receipt} label="Credit issued" value={`₹${Math.round(Number(summary.credit_issued || 0))}`} sub={`paid ₹${Math.round(Number(summary.credit_paid || 0))}`} />
              <LedgerMetric icon={IndianRupee} label="Outstanding" value={`₹${Math.round(Number(summary.outstanding || 0))}`} sub={`${summary.credit_count || 0} credit records`} />
            </div>
            <div className="border border-zinc-800 bg-zinc-950">
              <div className="border-b border-zinc-800 px-4 py-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">Timeline</p>
              </div>
              <div className="divide-y divide-zinc-800">
                {timeline.map((row, index) => (
                  <div key={`${row.kind}-${row.id}-${index}`} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-display text-lg font-semibold text-zinc-100">{row.title}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{row.kind} · {row.status || "-"} · {formatTs(row.ts)}</p>
                        {!!row.invoice_number && <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-orange-300">{row.invoice_number}</p>}
                      </div>
                      <p className={`font-display text-xl font-black ${row.kind === "refund" ? "text-red-400" : row.kind === "credit" ? "text-sky-300" : "text-orange-500"}`}>
                        ₹{Math.round(Number(row.balance_amount ?? row.amount ?? 0))}
                      </p>
                    </div>
                    {!!(row.items || []).length && (
                      <div className="mt-3 space-y-1">
                        {(row.items || []).slice(0, 4).map((item, itemIndex) => (
                          <div key={`${row.id}-item-${itemIndex}`} className="flex justify-between gap-3 font-mono text-[10px] text-zinc-500">
                            <span>{item.name || "Item"} x {item.qty || 1}</span>
                            <span>₹{Math.round(Number(item.subtotal || item.price || 0))}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {!!(row.payment_history || []).length && (
                      <div className="mt-3 border-t border-zinc-900 pt-2">
                        {(row.payment_history || []).map((payment, paymentIndex) => (
                          <div key={`${row.id}-pay-${paymentIndex}`} className="flex justify-between gap-3 font-mono text-[10px] text-emerald-300">
                            <span>{formatTs(payment.paid_at)} · {payment.method || "payment"}</span>
                            <span>₹{Math.round(Number(payment.amount || 0))}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {!timeline.length && <p className="px-4 py-10 text-center text-sm text-zinc-500">No customer activity found.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReceiptDrawer({ row, onClose }) {
  if (!row) return null;
  const print = () => window.print();
  const extra = row.extra || {};
  const seller = extra.seller_profile || {};
  const invoiceNumber = extra.invoice_number || row.invoice_number || row.ref || row.id;
  const items = row.items?.length ? row.items : [{ name: row.title, qty: 1, price: row.amount, subtotal: row.amount }];
  const subtotal = Number(extra.subtotal ?? items.reduce((sum, item) => sum + Number(item.subtotal ?? Number(item.qty || 1) * Number(item.price || 0)), 0));
  const totalLines = receiptTotalLines(extra);
  const total = Number(row.amount || 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md border border-zinc-800 bg-zinc-950 p-4 shadow-2xl print:border-0 print:bg-white print:text-black">
        <div className="mb-3 flex items-center justify-between print:hidden">
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-orange-500">Receipt preview</p>
          <button onClick={onClose} className="border border-zinc-800 p-2"><X className="h-4 w-4" /></button>
        </div>
        <div className="bg-white p-5 text-black">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-black uppercase tracking-widest">{seller.shop_name || "Mini Seller"}</p>
              <p className="mt-1 text-xs">{seller.address || ""}</p>
              <p className="text-xs">{[seller.city, seller.pincode].filter(Boolean).join(" ")}</p>
              {seller.gstin && <p className="text-xs">GSTIN: {seller.gstin}</p>}
            </div>
            <div className="text-right">
              <p className="font-black uppercase tracking-widest">Receipt</p>
              <p className="mt-1 text-xs">{invoiceNumber}</p>
              <p className="text-xs">{formatTs(row.ts)}</p>
            </div>
          </div>
          <div className="my-3 border-t border-dashed border-black" />
          <p className="font-bold">{row.customer_name || "Walk-in"}</p>
          <p className="text-xs">{row.customer_phone || ""}</p>
          {row.customer_email && <p className="text-xs">{row.customer_email}</p>}
          <div className="my-3 border-t border-dashed border-black" />
          {items.map((item, index) => {
            const qty = Number(item.qty || 1);
            const price = Number(item.price || item.subtotal || 0);
            const lineTotal = Number(item.subtotal ?? qty * price);
            return (
              <div key={index} className="mb-2 grid grid-cols-[1fr_auto] gap-3 text-sm">
                <span>
                  {item.name || row.title}
                  <span className="block text-xs text-zinc-600">{qty} x ₹{formatMoney(price)}</span>
                </span>
                <span>₹{formatMoney(lineTotal)}</span>
              </div>
            );
          })}
          <div className="my-3 border-t border-dashed border-black" />
          <div className="flex justify-between text-sm">
            <span>Subtotal</span><span>₹{formatMoney(subtotal)}</span>
          </div>
          {totalLines.map((line, index) => (
            <div key={`${line.label}-${index}`} className="mt-1 flex justify-between text-sm">
              <span>{line.label}</span>
              <span>{Number(line.amount || 0) < 0 ? "-" : ""}₹{formatMoney(Math.abs(Number(line.amount || 0)))}</span>
            </div>
          ))}
          <div className="flex justify-between font-black">
            <span>Total</span><span>₹{formatMoney(total)}</span>
          </div>
          <p className="mt-2 text-xs">Mode: {row.method || "-"}</p>
          <p className="mt-4 text-center text-xs">Thank you</p>
        </div>
        <button onClick={print} className="mt-3 flex w-full items-center justify-center gap-2 bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black print:hidden">
          <Printer className="h-4 w-4" /> Print
        </button>
      </div>
    </div>
  );
}

function EditBillModal({ bill, setBill, onSave, busy }) {
  if (!bill) return null;
  const update = (key, value) => setBill((current) => ({ ...current, [key]: value }));
  const updateItem = (id, key, value) => setBill((current) => ({
    ...current,
    items: (current.items || []).map((item) => item.id === id ? { ...item, [key]: value } : item),
  }));
  const addItem = () => setBill((current) => ({ ...current, items: [...(current.items || []), emptyItem()] }));
  const removeItem = (id) => setBill((current) => {
    const next = (current.items || []).filter((item) => item.id !== id);
    return { ...current, items: next.length ? next : [emptyItem()] };
  });
  const updateManualLine = (id, key, value) => setBill((current) => ({
    ...current,
    manual_lines: (current.manual_lines || []).map((line) => line.id === id ? { ...line, [key]: value } : line),
  }));
  const addManualLine = () => setBill((current) => ({ ...current, manual_lines: [...(current.manual_lines || []), emptyManualLine()] }));
  const removeManualLine = (id) => setBill((current) => ({ ...current, manual_lines: (current.manual_lines || []).filter((line) => line.id !== id) }));
  const itemTotal = (bill.items || []).reduce((sum, item) => sum + Number(item.qty || 0) * Number(item.price || 0), 0);
  const manualTotal = (bill.manual_lines || []).reduce((sum, line) => sum + Number(line.amount || 0), 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto border border-zinc-800 bg-zinc-950 p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-orange-500">Edit bill</p>
            <h3 className="mt-1 font-display text-2xl font-black">Correct Mini bill safely</h3>
            <p className="mt-1 text-sm text-zinc-500">Every edit needs a reason and is saved in bill audit history.</p>
          </div>
          <button onClick={() => setBill(null)} className="border border-zinc-800 p-2 text-zinc-300 hover:border-orange-500 hover:text-orange-500">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <LedgerField label="Customer name" value={bill.customer_name || ""} onChange={(v) => update("customer_name", v)} />
          <LedgerField label="Mobile" value={bill.customer_phone || ""} onChange={(v) => update("customer_phone", v)} />
          <LedgerField label="Email" type="email" value={bill.customer_email || ""} onChange={(v) => update("customer_email", v.toLowerCase())} />
          <LedgerField label="Bill title" value={bill.title || ""} onChange={(v) => update("title", v)} />
        </div>

        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">Items</p>
            <button type="button" onClick={addItem} className="inline-flex items-center gap-1 border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
              <Plus className="h-3 w-3" /> Add item
            </button>
          </div>
          {(bill.items || []).map((item, index) => (
            <div key={item.id} className="grid gap-2 md:grid-cols-[1fr_90px_120px_42px]">
              <input value={item.name || ""} onChange={(e) => updateItem(item.id, "name", e.target.value)} placeholder={`Item ${index + 1}`} className="border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500" />
              <input type="number" min="1" value={item.qty || ""} onChange={(e) => updateItem(item.id, "qty", e.target.value)} placeholder="Qty" className="border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500" />
              <input type="number" min="0" value={item.price || ""} onChange={(e) => updateItem(item.id, "price", e.target.value)} placeholder="Rate" className="border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500" />
              <button type="button" onClick={() => removeItem(item.id)} className="border border-zinc-800 text-red-300 hover:border-red-500 hover:bg-red-500 hover:text-black">
                <Trash2 className="mx-auto h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">Manual lines</p>
            <button type="button" onClick={addManualLine} className="inline-flex items-center gap-1 border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
              <Plus className="h-3 w-3" /> Add line
            </button>
          </div>
          {(bill.manual_lines || []).map((line) => (
            <div key={line.id} className="grid gap-2 md:grid-cols-[1fr_140px_42px]">
              <input value={line.label || ""} onChange={(e) => updateManualLine(line.id, "label", e.target.value)} placeholder="Discount, packing, delivery..." className="border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500" />
              <input type="number" value={line.amount || ""} onChange={(e) => updateManualLine(line.id, "amount", e.target.value)} placeholder="Amount" className="border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500" />
              <button type="button" onClick={() => removeManualLine(line.id)} className="border border-zinc-800 text-red-300 hover:border-red-500 hover:bg-red-500 hover:text-black">
                <Trash2 className="mx-auto h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <label className="mt-5 block">
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">Edit reason</span>
          <textarea value={bill.reason || ""} onChange={(e) => update("reason", e.target.value)} className="mt-2 min-h-20 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none placeholder:text-zinc-700 focus:border-orange-500" placeholder="Example: wrong quantity entered at counter" />
        </label>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 pt-4">
          <p className="font-mono text-xs text-zinc-500">Preview subtotal ₹{Math.round(itemTotal)} · manual ₹{Math.round(manualTotal)}</p>
          <button disabled={busy} onClick={onSave} className="bg-orange-500 px-5 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
            {busy ? "Saving..." : "Save edit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function receiptTotalLines(extra = {}) {
  const grouped = new Map();
  const add = (label, amount) => {
    const clean = String(label || "").trim();
    const value = Number(amount || 0);
    if (!clean || value === 0) return;
    grouped.set(clean, (grouped.get(clean) || 0) + value);
  };
  (extra.manual_lines || []).forEach((line) => add(line.label || line.name, line.amount));
  (extra.billing_addons || []).forEach((line) => {
    if (String(line.key || "").toLowerCase() === "manual") return;
    add(line.label || line.name, line.amount);
  });
  return Array.from(grouped, ([label, amount]) => ({ label, amount }));
}

function CreditSaleNotes({ draft, updateDraft, items, updateItem, addItem, removeItem, total, credits, busy, onSave, onPay, onPaymentLink, paymentDrafts, updatePaymentDraft, onCustomer, issued, settled, pending }) {
  const paidAmount = Number(draft.paid_amount || 0);
  const balance = Math.max(0, total - paidAmount);
  const customerOutstanding = Object.values(credits.reduce((acc, credit) => {
    const key = `${credit.customer_phone || ""}-${credit.customer_name || "Customer"}`;
    const current = acc[key] || { name: credit.customer_name || "Customer", phone: credit.customer_phone || "", pending: 0, count: 0 };
    current.pending += Number(credit.balance_amount || 0);
    current.count += credit.status !== "PAID" ? 1 : 0;
    acc[key] = current;
    return acc;
  }, {})).filter((row) => row.pending > 0);
  return (
    <div className="grid gap-6 p-5 lg:grid-cols-[1fr_420px]">
      <div className="space-y-5">
        <div className="grid gap-px bg-zinc-800 sm:grid-cols-3">
          <LedgerMetric icon={Receipt} label="Credit issued" value={`₹${Math.round(issued || 0)}`} sub={`${credits.length} total records`} />
          <LedgerMetric icon={IndianRupee} label="Credit settled" value={`₹${Math.round(settled || 0)}`} sub="paid by credit customers" />
          <LedgerMetric icon={IndianRupee} label="Still pending" value={`₹${Math.round(pending || 0)}`} sub="collectable balance" />
        </div>
        <div className="border border-zinc-800 bg-zinc-950 p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Credit sale</p>
          <h3 className="mt-1 font-display text-2xl font-semibold tracking-tight">Credit sale register</h3>
          <p className="mt-2 text-sm text-zinc-500">Use this when a customer takes products now and pays later. It stays separate from paid transaction history until settled.</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <LedgerField label="Customer name" value={draft.customer_name} onChange={(v) => updateDraft("customer_name", v)} />
            <LedgerField label="Mobile" value={draft.customer_phone} onChange={(v) => updateDraft("customer_phone", v)} />
            <LedgerField label="Email for payment links" type="email" value={draft.customer_email || ""} onChange={(v) => updateDraft("customer_email", v.toLowerCase())} />
          </div>
          <label className="mt-3 block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Address</span>
            <textarea value={draft.customer_address} onChange={(e) => updateDraft("customer_address", e.target.value)} className="mt-2 min-h-20 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none placeholder:text-zinc-700 focus:border-orange-500" placeholder="Customer address" />
          </label>
        </div>

        <div className="border border-zinc-800 bg-zinc-950">
          <div className="flex items-center justify-between gap-3 border-b border-zinc-800 px-4 py-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-orange-500">Product list</p>
              <p className="mt-1 text-xs text-zinc-500">Products sold on credit.</p>
            </div>
            <button type="button" onClick={addItem} className="inline-flex items-center gap-2 border border-orange-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black">
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>
          <div className="divide-y divide-zinc-800">
            {items.map((item, index) => (
              <div key={item.id} className="grid gap-3 p-4 sm:grid-cols-[1fr_90px_120px_42px] sm:items-end">
                <LedgerField label={`Product ${index + 1}`} value={item.name} onChange={(v) => updateItem(item.id, "name", v)} />
                <LedgerField label="Qty" type="number" value={item.qty} onChange={(v) => updateItem(item.id, "qty", v)} />
                <LedgerField label="Price" type="number" value={item.price} onChange={(v) => updateItem(item.id, "price", v)} />
                <button type="button" onClick={() => removeItem(item.id)} className="h-[46px] border border-zinc-800 text-zinc-500 hover:border-red-400 hover:text-red-300" aria-label="Remove credit item">
                  <Trash2 className="mx-auto h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <LedgerField label="Already paid" type="number" value={draft.paid_amount} onChange={(v) => updateDraft("paid_amount", v)} />
          <LedgerField label="Note" value={draft.note} onChange={(v) => updateDraft("note", v)} />
        </div>
      </div>

      <div className="space-y-5">
        <div className="border border-orange-500/30 bg-orange-500/5 p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Credit summary</p>
          <p className="mt-4 font-display text-4xl font-semibold tracking-tight text-orange-500">₹{Math.round(balance)}</p>
          <p className="mt-1 text-sm text-zinc-500">Balance unpaid from ₹{Math.round(total)} total.</p>
          <button disabled={busy} onClick={onSave} className="mt-5 w-full bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
            Save credit sale
          </button>
        </div>

        <div className="border border-zinc-800 bg-zinc-950">
          <div className="border-b border-zinc-800 px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Credit register</p>
          </div>
          {!!customerOutstanding.length && (
            <div className="border-b border-zinc-800 bg-black/40 px-4 py-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">Customer-wise outstanding</p>
              <div className="mt-3 space-y-2">
                {customerOutstanding.slice(0, 5).map((row) => (
                  <div key={`${row.phone}-${row.name}`} className="flex items-center justify-between gap-3 text-xs">
                    <span className="truncate text-zinc-300">{row.name} <span className="font-mono text-zinc-600">{row.phone}</span></span>
                    <span className="font-display font-semibold text-orange-400">₹{Math.round(row.pending)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="max-h-[520px] divide-y divide-zinc-800 overflow-y-auto">
            {credits.map((credit) => (
              <div key={credit.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <button type="button" onClick={() => onCustomer?.(credit)} className="text-left font-display text-lg font-semibold text-zinc-100 hover:text-orange-400">
                      {credit.customer_name}
                    </button>
                    <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{credit.customer_phone || "No phone"} · {credit.status}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-600">{credit.customer_email || "No email for link"}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-600">
                      Opened {formatTs(credit.credit_started_at || credit.created_at)}
                    </p>
                  </div>
                  <p className={`font-display text-xl font-semibold ${credit.status === "PAID" ? "text-emerald-400" : "text-orange-500"}`}>₹{Math.round(Number(credit.balance_amount || 0))}</p>
                </div>
                {credit.status === "PAID" && (
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-emerald-400">
                    Settled {formatTs(credit.settled_at || credit.last_paid_at)} {credit.transaction_ref ? `· ${credit.transaction_ref}` : ""}
                  </p>
                )}
                <p className="mt-2 text-xs text-zinc-500">{credit.customer_address || "No address"}</p>
                <div className="mt-3 space-y-1">
                  {(credit.items || []).map((item, index) => (
                    <div key={`${credit.id}-${index}`} className="flex justify-between gap-3 font-mono text-[10px] text-zinc-400">
                      <span>{item.name} x {item.qty}</span>
                      <span>₹{Math.round(Number(item.subtotal || 0))}</span>
                    </div>
                  ))}
                </div>
                {!!(credit.payment_history || []).length && (
                  <div className="mt-3 space-y-1 border-t border-zinc-900 pt-2">
                    {(credit.payment_history || []).map((payment, index) => (
                      <div key={`${credit.id}-payment-${index}`} className="flex justify-between gap-3 font-mono text-[10px] text-emerald-300">
                        <span>{formatTs(payment.paid_at)} · {payment.method || "payment"}</span>
                        <span>₹{Math.round(Number(payment.amount || 0))}</span>
                      </div>
                    ))}
                  </div>
                )}
                {credit.status !== "PAID" && (
                  <div className="mt-3 space-y-2 border border-zinc-800 bg-black/40 p-3">
                    <div className="grid gap-2 sm:grid-cols-[1fr_110px]">
                      <input
                        type="number"
                        value={paymentDrafts?.[credit.id]?.amount || ""}
                        onChange={(e) => updatePaymentDraft(credit.id, "amount", e.target.value)}
                        placeholder="Payment amount"
                        className="border border-zinc-800 bg-zinc-950 px-3 py-2 font-mono text-xs text-zinc-100 outline-none placeholder:text-zinc-700 focus:border-orange-500"
                      />
                      <select
                        value={paymentDrafts?.[credit.id]?.method || "cash"}
                        onChange={(e) => updatePaymentDraft(credit.id, "method", e.target.value)}
                        className="border border-zinc-800 bg-zinc-950 px-2 py-2 font-mono text-xs text-zinc-100 outline-none focus:border-orange-500"
                      >
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="card">Card</option>
                        <option value="razorpay">Razorpay</option>
                      </select>
                    </div>
                    <input
                      type="email"
                      value={paymentDrafts?.[credit.id]?.email ?? credit.customer_email ?? ""}
                      onChange={(e) => updatePaymentDraft(credit.id, "email", e.target.value.toLowerCase())}
                      placeholder="Customer email for payment link"
                      className="w-full border border-zinc-800 bg-zinc-950 px-3 py-2 font-mono text-xs text-zinc-100 outline-none placeholder:text-zinc-700 focus:border-orange-500"
                    />
                    <input
                      value={paymentDrafts?.[credit.id]?.note || ""}
                      onChange={(e) => updatePaymentDraft(credit.id, "note", e.target.value)}
                      placeholder="Payment note"
                      className="w-full border border-zinc-800 bg-zinc-950 px-3 py-2 font-mono text-xs text-zinc-100 outline-none placeholder:text-zinc-700 focus:border-orange-500"
                    />
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button disabled={busy} onClick={() => onPay(credit, false)} className="border border-emerald-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300 hover:bg-emerald-500 hover:text-black disabled:opacity-50">
                        Collect payment
                      </button>
                      <button disabled={busy} onClick={() => onPaymentLink(credit, false)} className="border border-sky-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-sky-300 hover:bg-sky-500 hover:text-black disabled:opacity-50">
                        Send payment link
                      </button>
                      <button disabled={busy} onClick={() => onPay(credit, true)} className="border border-orange-500 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-50">
                        Settle full balance
                      </button>
                      <button disabled={busy} onClick={() => onPaymentLink(credit, true)} className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-sky-500 hover:text-sky-300 disabled:opacity-50">
                        Link full balance
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {!credits.length && <p className="px-4 py-10 text-center text-sm text-zinc-500">No credit sales yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function LedgerMetric({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-zinc-950 p-5">
      <Icon className="mb-4 h-5 w-5 text-orange-500" />
      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</p>
      <p className="mt-2 font-display text-3xl font-black">{value}</p>
      <p className="mt-1 text-sm text-zinc-500">{sub}</p>
    </div>
  );
}

function LedgerTab({ active, onClick, children, className = "" }) {
  return (
    <button onClick={onClick} className={`shrink-0 px-4 py-3 font-mono text-[10px] uppercase tracking-widest sm:px-5 ${active ? "bg-orange-500 text-black" : "text-zinc-400 hover:text-orange-500"} ${className}`}>
      {children}
    </button>
  );
}

function LedgerFilters({ filters, upd, onSearch, busy }) {
  return (
    <div className="grid gap-3 lg:grid-cols-[1fr_160px_150px_150px_auto]">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
        <input value={filters.q} onChange={(e) => upd("q", e.target.value)} placeholder="Search customer, product, seller, method..." className="w-full border border-zinc-800 bg-zinc-950 py-3 pl-10 pr-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500" />
      </div>
      <select value={filters.period} onChange={(e) => upd("period", e.target.value)} className="border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500">
        {Object.entries(periods).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select>
      <input type="date" value={filters.date_from} onChange={(e) => upd("date_from", e.target.value)} className="border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500" />
      <input type="date" value={filters.date_to} onChange={(e) => upd("date_to", e.target.value)} className="border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500" />
      <button onClick={onSearch} disabled={busy} className="bg-orange-500 px-5 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
        Search
      </button>
    </div>
  );
}

function StorageMeter({ usage }) {
  if (!usage) return null;
  const percent = Math.max(0, Math.min(100, Number(usage.percent || 0)));
  const used = Number(usage.used_mb || 0);
  const remaining = Number(usage.remaining_mb || 0);
  const limit = Number(usage.limit_gb || 10);
  const tone = usage.warning_level === "full" ? "border-red-500/60" : usage.warning_level === "danger" ? "border-orange-500/60" : "border-zinc-800";
  return (
    <section className={`flex flex-wrap items-center justify-between gap-4 border bg-zinc-950 px-5 py-4 ${tone}`}>
      <div className="flex items-center gap-4">
        <div className="grid h-16 w-16 place-items-center rounded-full" style={{ background: `conic-gradient(#f97316 ${percent * 3.6}deg, #27272a 0deg)` }}>
          <div className="grid h-12 w-12 place-items-center rounded-full bg-zinc-950">
            <span className="font-mono text-xs font-bold text-orange-300">{Math.round(percent)}%</span>
          </div>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Mini storage</p>
          <p className="mt-1 font-display text-xl font-semibold text-zinc-100">{used < 1 ? `${Math.round(used * 1024)} KB` : `${used.toFixed(2)} MB`} used</p>
          <p className="mt-1 text-xs text-zinc-500">{remaining.toFixed(2)} MB left from {limit}GB included storage</p>
        </div>
      </div>
      <p className="max-w-md text-xs leading-relaxed text-zinc-500">
        {usage.write_locked ? "Storage is full. New bills are blocked until cleanup or upgrade." : "Soft-deleted and archived transactions are removed from active Mini storage and dashboard totals."}
      </p>
    </section>
  );
}

function MiniTrialMeter({ subscription }) {
  if (!subscription || subscription.plan !== "terminal_ledger" || subscription.trial_type !== "transaction_limit") return null;
  const used = Number(subscription.trial_transactions_used || 0);
  const limit = Number(subscription.trial_transaction_limit || 99);
  const remaining = Number(subscription.trial_transactions_remaining ?? Math.max(0, limit - used));
  const percent = limit > 0 ? Math.min(100, used * 100 / limit) : 0;
  const exhausted = subscription.status === "trial_exhausted" || remaining <= 0;
  return (
    <section className={`flex flex-wrap items-center justify-between gap-4 border px-5 py-4 ${exhausted ? "border-red-500/60 bg-red-950/20" : percent >= 80 ? "border-orange-500/60 bg-orange-950/20" : "border-zinc-800 bg-zinc-950"}`}>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Mini free trial</p>
        <p className="mt-1 font-display text-xl font-semibold text-zinc-100">{used} / {limit} free transactions used</p>
        <p className="mt-1 text-xs text-zinc-500">{exhausted ? "Trial complete. Subscribe to continue creating bills." : `${remaining} free transactions remaining before subscription is required.`}</p>
      </div>
      <div className="w-full max-w-xs space-y-3">
        <div className="h-2 overflow-hidden bg-zinc-800">
          <div className={`h-full ${exhausted ? "bg-red-400" : "bg-orange-500"}`} style={{ width: `${percent}%` }} />
        </div>
        {exhausted && (
          <a href="/#pricing" target="_blank" rel="noreferrer" className="block border border-red-400/60 px-3 py-2 text-center font-mono text-[10px] uppercase tracking-widest text-red-200 hover:bg-red-500 hover:text-black">
            Subscribe / extend Mini
          </a>
        )}
      </div>
    </section>
  );
}

function StorageManager({ usage, archives, draft, setDraft, busy, selectedCount, onArchiveSelected, onDownloadArchive, onViewArchive, onSaveSettings, onRunCleanup }) {
  if (!usage) return null;
  const update = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  return (
    <section className="grid gap-4 border border-zinc-800 bg-zinc-900/40 p-4 lg:grid-cols-[1.2fr_1fr]">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Storage cleanup</p>
            <h3 className="font-display text-xl font-semibold">Archive or upgrade</h3>
          </div>
          <button type="button" disabled={!selectedCount || busy} onClick={onArchiveSelected} className="border border-orange-500/60 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-40">
            Archive selected ({selectedCount})
          </button>
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          {(usage.addons || []).map((addon) => (
            <div key={addon.name} className="border border-zinc-800 bg-black p-3">
              <p className="font-display text-lg font-semibold text-zinc-100">{addon.name}</p>
              <p className="mt-1 font-mono text-xs text-orange-300">₹{addon.price}/mo</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-500">Archive old records to continue without extra storage, or upgrade if the merchant wants older data live inside Mini.</p>
      </div>
      <div className="space-y-3 border border-zinc-800 bg-black p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Auto policy</p>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={Boolean(draft.auto_archive_enabled)} onChange={(e) => update("auto_archive_enabled", e.target.checked)} />
          Auto archive old records
        </label>
        <div className="grid gap-2 sm:grid-cols-2">
          <LedgerField label="Archive after days" type="number" value={draft.archive_after_days} onChange={(v) => update("archive_after_days", v)} />
          <LedgerField label="Purge after days" type="number" value={draft.purge_after_days} onChange={(v) => update("purge_after_days", v)} />
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={onSaveSettings} className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500 disabled:opacity-40">Save policy</button>
          <button type="button" disabled={busy} onClick={onRunCleanup} className="border border-orange-500/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-40">Run cleanup</button>
        </div>
        {!!archives.length && (
          <div className="max-h-32 space-y-2 overflow-y-auto border-t border-zinc-900 pt-3">
            {archives.slice(0, 4).map((archive) => (
              <div key={archive.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 text-xs text-zinc-400">
                <button type="button" onClick={() => onViewArchive?.(archive)} className="min-w-0 text-left hover:text-orange-300">
                  <span className="block truncate">{formatTs(archive.created_at)} · {archive.record_count || 0} records</span>
                </button>
                <button type="button" onClick={() => onViewArchive?.(archive)} className="border border-zinc-800 px-2 py-1 font-mono text-[9px] uppercase tracking-widest hover:border-orange-500 hover:text-orange-300">View</button>
                <button type="button" onClick={() => onDownloadArchive?.(archive)} className="inline-flex items-center gap-1 border border-zinc-800 px-2 py-1 font-mono text-[9px] uppercase tracking-widest hover:border-orange-500 hover:text-orange-300">
                  <Download className="h-3 w-3" /> CSV
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ArchiveViewer({ archive, onClose, onDownload }) {
  if (!archive) return null;
  const records = Array.isArray(archive.records) ? archive.records : [];
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="max-h-[86vh] w-full max-w-5xl overflow-hidden border border-zinc-800 bg-zinc-950 shadow-2xl">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-zinc-800 p-5">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Archive records</p>
            <h3 className="mt-1 font-display text-2xl font-black">{archive.record_count || records.length || 0} Mini transaction backup</h3>
            <p className="mt-1 text-xs text-zinc-500">{formatTs(archive.created_at)} · {archive.reason || "No reason captured"}</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => onDownload?.(archive)} className="inline-flex items-center gap-2 border border-orange-500/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black">
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
            <button type="button" onClick={onClose} className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">Close</button>
          </div>
        </div>
        <div className="max-h-[62vh] overflow-auto p-5">
          <table className="min-w-full divide-y divide-zinc-800 text-left text-sm">
            <thead className="bg-zinc-950 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <tr>
                <th className="px-3 py-2">Invoice</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {records.map((row, index) => (
                <tr key={row.id || index} className="text-zinc-300">
                  <td className="px-3 py-2 font-mono text-xs">{row.invoice_number || row.id || "-"}</td>
                  <td className="px-3 py-2">{row.customer_name || row.customer_phone || "Walk-in"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.status || "-"}</td>
                  <td className="px-3 py-2 text-right font-display font-semibold text-orange-400">₹{formatMoney(row.total || row.amount || 0)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-500">{formatTs(row.created_at || row.paid_at)}</td>
                </tr>
              ))}
              {!records.length && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-sm text-zinc-500">This archive has no embedded preview records. Download CSV to inspect it.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BulkTransactionBar({ count, visibleCount, busy, onSelectVisible, onArchive }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border border-zinc-800 bg-zinc-950 px-4 py-3">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        {count ? `${count} selected` : `${visibleCount} Mini bills in this filter`}
      </p>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={onSelectVisible} disabled={!visibleCount || busy} className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500 disabled:opacity-40">
          Select all filtered
        </button>
        <button type="button" onClick={onArchive} disabled={!count || busy} className="inline-flex items-center gap-2 border border-orange-500/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-40">
          <Download className="h-3.5 w-3.5" /> Archive selected
        </button>
      </div>
    </div>
  );
}

function LedgerTable({ rows, empty, onRefund, onCustomer, onResend, onRetryPayment, onCollectOffline, onPrint, onEdit, onCancel, selectedIds = [], selectableIds = [], onToggleSelect, onToggleSelectVisible }) {
  const selectableSet = new Set(selectableIds);
  const allVisibleSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
  return (
    <div className="overflow-x-auto border border-zinc-800">
      <table className="min-w-full divide-y divide-zinc-800 text-left">
        <thead className="bg-zinc-950">
          <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            {onToggleSelect && (
              <th className="px-4 py-3">
                <input type="checkbox" checked={allVisibleSelected} onChange={onToggleSelectVisible} aria-label="Select all visible Mini bills" />
              </th>
            )}
            <th className="px-4 py-3">Date/time</th>
            <th className="px-4 py-3">Product / ref</th>
            <th className="px-4 py-3">Customer</th>
            <th className="px-4 py-3">Mode</th>
            <th className="px-4 py-3 text-right">Amount</th>
            {onRefund && <th className="px-4 py-3 text-right">Action</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-800">
          {rows.map((row) => {
            const status = String(row.status || row.extra?.status || "").toUpperCase();
            const pending = row.kind === "ledger" && !row.paid && status !== "PAYMENT_FAILED";
            const failed = row.kind === "ledger" && status === "PAYMENT_FAILED";
            const success = row.kind === "ledger" && (row.paid || status === "PAID");
            const subtotal = Number(row.extra?.subtotal ?? 0);
            const taxTotal = Number(row.extra?.tax_total ?? row.extra?.gst_amount ?? 0);
            return (
            <tr key={`${row.kind}-${row.id}`} className="bg-zinc-950/40 text-sm text-zinc-300">
              {onToggleSelect && (
                <td className="px-4 py-3">
                  {selectableSet.has(row.id) ? (
                    <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => onToggleSelect(row.id)} aria-label={`Select ${row.ref || row.id}`} />
                  ) : null}
                </td>
              )}
              <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-zinc-500">{formatTs(row.ts)}</td>
              <td className="px-4 py-3">
                <p className="font-display font-bold text-zinc-100">{row.title || row.kind}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-orange-300">{row.kind} · {row.ref || row.id}</p>
                {row.kind === "ledger" && (subtotal > 0 || taxTotal > 0) && (
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-500">
                    Subtotal ₹{formatMoney(subtotal || row.amount)} · Tax ₹{formatMoney(taxTotal)} · Total ₹{formatMoney(row.amount)}
                  </p>
                )}
                {success && <p className="mt-1 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300"><span className="h-2 w-2 rounded-full bg-emerald-300" /> Paid successfully</p>}
                {pending && <p className="mt-1 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-yellow-300"><span className="h-2 w-2 animate-pulse rounded-full bg-yellow-300" /> Payment pending</p>}
                {failed && <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-red-300">Payment failed</p>}
              </td>
              <td className="px-4 py-3">
                <button type="button" onClick={() => onCustomer?.(row)} className="text-left hover:text-orange-400">
                  {row.customer_name || "Walk-in"}
                </button>
                <p className="font-mono text-[10px] text-zinc-500">{row.customer_phone || "No phone"}</p>
              </td>
              <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{row.method || "-"}</td>
              <td className={`px-4 py-3 text-right font-display text-lg font-black ${success ? "text-emerald-300" : failed ? "text-red-300" : pending ? "text-yellow-300" : row.kind === "credit" ? "text-sky-300" : Number(row.amount || 0) < 0 ? "text-red-400" : "text-orange-500"}`}>₹{formatMoney(row.amount)}</td>
              {onRefund && (
                <td className="px-4 py-3 text-right">
                  <div className="flex flex-wrap justify-end gap-2">
                    {row.kind === "ledger" && !pending && !failed && (
                      <a href={backendUrl(`/api/public/ledger/bills/${row.id}/invoice.pdf`)} target="_blank" rel="noreferrer" className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                        Invoice
                      </a>
                    )}
                    {row.kind === "ledger" && !pending && !failed && (
                      <button onClick={() => onPrint?.(row)} className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                        <Printer className="inline h-3 w-3" />
                      </button>
                    )}
                    {row.kind === "ledger" && !pending && !failed && (
                      <button onClick={() => onResend?.(row)} className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                        <Mail className="inline h-3 w-3" />
                      </button>
                    )}
                    {row.kind === "ledger" && (
                      <button onClick={() => onEdit?.(row)} className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                        Edit
                      </button>
                    )}
                    {row.kind === "ledger" && !pending && !failed && Number(row.amount || 0) > 0 && !row.extra?.refund_status ? (
                      <button onClick={() => onRefund(row)} className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                        Refund
                      </button>
                    ) : null}
                    {pending && <span className="border border-yellow-500/40 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-yellow-300">Waiting</span>}
                    {failed && <span className="border border-red-500/50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-red-300">Failed</span>}
                    {row.kind === "ledger" && (pending || failed) && (
                      <button onClick={() => onRetryPayment?.(row)} className="border border-emerald-500/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300 hover:bg-emerald-500 hover:text-black">
                        Resend link
                      </button>
                    )}
                    {row.kind === "ledger" && (pending || failed) && (
                      <button onClick={() => onCollectOffline?.(row)} className="border border-sky-500/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-sky-300 hover:bg-sky-500 hover:text-black">
                        Paid offline
                      </button>
                    )}
                    {success && <span className="border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300">Paid</span>}
                    {row.kind === "ledger" && (
                      <button onClick={() => onCancel?.(row)} className="border border-red-500/50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-red-300 hover:bg-red-500 hover:text-black">
                        Cancel
                      </button>
                    )}
                    {row.kind === "credit" && <span className="border border-sky-500/40 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-sky-300">Pending</span>}
                    {row.kind !== "ledger" && row.kind !== "credit" && <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">-</span>}
                  </div>
                </td>
              )}
            </tr>
          )})}
          {!rows.length && (
            <tr>
              <td colSpan={(onRefund ? 6 : 5) + (onToggleSelect ? 1 : 0)} className="px-4 py-10 text-center text-sm text-zinc-500">{empty}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function LedgerField({ label, value, onChange, type = "text", list, placeholder, disabled = false }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      <input disabled={disabled} type={type} list={list} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || label} className="mt-2 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none placeholder:text-zinc-700 focus:border-orange-500 disabled:opacity-60" />
    </label>
  );
}

function formatTs(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 19);
  return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}
